/**
 * 相机管理器。
 * 负责相机实例创建与生命周期管理、轨道控制器配置、视口 Gizmo 管理、
 * 鼠标交互模式切换、相机状态保存与恢复等操作。
 */
import type {WebGPURenderer} from 'three/webgpu';
import * as THREE from 'three/webgpu';
import type {Viewer} from '../viewer';
import {
    buildStandardViewCameraFrame,
    CameraProjectionType,
    listStandardViewDefinitions,
    type StandardViewCameraFrame,
    StandardViewType,
    ViewOrientationMesh,
} from './view-orientation';
import {isDatumAxis, isDatumPlaneMesh, isDatumPlaneObject, isDatumPoint,} from '../reference';
import {isSketchLineObject} from '../mesh/guards/isSketchLineObject';
import {EMPTY, merge, type Observable, Subject} from 'rxjs';
import {ViewOrientationInteractionController} from './ViewOrientationInteractionController';
import {
    CameraEventName,
    type CameraProjectionTypeChangedEventPayload,
    type CameraStateChangedEventPayload,
} from '../eventBus';
import {useEvent} from '../hooks';
import {CameraControls} from "./CameraControls";
import {BoundingBoxCameraFocusSolver} from './BoundingBoxCameraFocusSolver';
import {CameraProjectionDistanceConverter} from './CameraProjectionDistanceConverter';
import {CameraModelSpatialQuery} from './CameraModelSpatialQuery';
import {CameraClippingBoundsCache} from './CameraClippingBoundsCache';
import {RotationCenterDebugVisual, RotationCenterDebugVisualState} from './RotationCenterDebugVisual';
import {
    RotationCenterTopologyHighlightController,
    type RotationCenterTopologyHighlightTarget
} from './RotationCenterTopologyHighlightController';
import {
    ViewportRotationFocusResolver,
    type ViewportRotationFocusResult,
    ViewportRotationFocusTargetKind,
} from './ViewportRotationFocusResolver';

import {
    ViewportGizmo,
    ViewportGizmoCameraAnimator,
    ViewportGizmoCameraRotationSolver,
    type ViewportGizmoControlActionTriggeredEvent,
    ViewportGizmoEventType,
    type ViewportGizmoOrbitDragChangedEvent,
    type ViewportGizmoStandardViewSelectedEvent,
} from './viewport-gizmo';


/**
 * 相机管理器配置。
 */
export interface CameraManagerOptions {
    /** 初始相机位置。 */
    cameraPosition: [number, number, number];
    /** 透视相机视角。 */
    fov: number;
}


/**
 * 标准视图预览帧构建选项。
 */
export interface StandardViewPreviewFrameOptions {
    /** 预览透视相机垂直视场角。 */
    fov?: number;
    /** 预览透视相机宽高比。 */
    aspect?: number;
}



/**
 * Inspector 参数编辑器最小协议。
 *
 * 设计说明：
 * 1. three.js Inspector examples 未提供稳定导出的 TypeScript 类型；
 * 2. CameraManager 只依赖 `name()` 与 `listen()` 两个链式能力；
 * 3. 用最小协议表达依赖边界，避免 CameraManager 直接耦合 Inspector 内部实现类。
 */
interface CameraDebugParameterEditor {
    /**
     * 设置参数显示名称。
     * @param name 显示名称。
     * @returns 当前编辑器。
     */
    name(name: string): CameraDebugParameterEditor;

    /**
     * 监听对象属性变化并同步到 UI。
     * @returns 当前编辑器。
     */
    listen(): CameraDebugParameterEditor;
}

/**
 * Inspector 参数分组最小协议。
 */
interface CameraDebugParameterGroup {
    /** 参数分组对应的 UI 列表项。 */
    paramList: {
        /** 参数分组根 DOM。 */
        domElement: HTMLElement;
    };

    /**
     * 添加一个参数编辑器。
     * @param object 参数来源对象。
     * @param property 参数属性名。
     * @param params 可选数值范围。
     * @returns 参数编辑器。
     */
    add(object: Record<string, unknown>, property: string, ...params: number[]): CameraDebugParameterEditor;

    /**
     * 添加子分组。
     * @param name 子分组名称。
     * @returns 子分组。
     */
    addFolder(name: string): CameraDebugParameterGroup;
}

/**
 * 正交相机调试面板输出的当前状态。
 */
interface OrthographicCameraDebugState {
    /** 视锥体左边界。 */
    left: number;
    /** 视锥体右边界。 */
    right: number;
    /** 视锥体上边界。 */
    top: number;
    /** 视锥体下边界。 */
    bottom: number;
    /** 近裁剪面。 */
    near: number;
    /** 远裁剪面。 */
    far: number;
    /** 正交相机缩放。 */
    zoom: number;
    /** 相机位置。 */
    position: {
        /** X 轴坐标。 */
        x: number;
        /** Y 轴坐标。 */
        y: number;
        /** Z 轴坐标。 */
        z: number;
    };
    /** 相机欧拉旋转。 */
    rotation: {
        /** X 轴旋转弧度。 */
        x: number;
        /** Y 轴旋转弧度。 */
        y: number;
        /** Z 轴旋转弧度。 */
        z: number;
    };
    /** 相机上方向。 */
    up: {
        /** X 轴分量。 */
        x: number;
        /** Y 轴分量。 */
        y: number;
        /** Z 轴分量。 */
        z: number;
    };
}


/**
 * 鼠标模式枚举。
 */
export enum MouseMode {
    /** 选择模式。 */
    Select = 'select',
    /** 旋转模式。 */
    Rotate = 'rotate',
    /** 平移模式。 */
    Pan = 'pan',
    /** 缩放模式。 */
    Zoom = 'zoom',
}


/**
 * 当前视口真实模型覆盖状态。
 */
enum ViewportRotationCoverageState {
    /** 当前视口内没有可用于旋转中心的真实模型。 */
    Empty = 'empty',
    /** 所有真实模型都完整位于当前屏幕内。 */
    AllInside = 'all-inside',
    /** 至少有一个真实模型被当前屏幕裁切或完全位于屏幕外。 */
    Clipped = 'clipped',
}



/**
 * 正交相机动态裁剪监听的场景子对象事件类型。
 */
enum OrthographicClippingSceneEventType {
    /** 场景直接子对象已添加。 */
    ChildAdded = 'childadded',
    /** 场景直接子对象已移除。 */
    ChildRemoved = 'childremoved',
}



/**
 * 相机管理器。
 * 职责：相机实例创建、轨道控制器配置、视口 Gizmo、鼠标交互模式切换、
 * 相机状态保存与恢复、窗口尺寸变化处理等。
 */
export class CameraManager {
    private static readonly MAIN_PERSPECTIVE_CAMERA_DEBUG_NAME = '主透视相机';
    private static readonly MAIN_ORTHOGRAPHIC_CAMERA_DEBUG_NAME = '主正交相机';

    /**
     * 世界坐标轴候选 up 方向集（±X / ±Y / ±Z 共6个）。
     *
     * 设计说明：
     * 预览相机的 up 必须是这6个方向之一，不允许出现中间值，
     * 从而保证 up 的变化永远是 90° 的整数倍，与 SolidWorks 行为一致。
     */
    private static readonly CANDIDATE_UP_AXES: readonly THREE.Vector3[] = [
        new THREE.Vector3(1, 0, 0),
        new THREE.Vector3(-1, 0, 0),
        new THREE.Vector3(0, 1, 0),
        new THREE.Vector3(0, -1, 0),
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(0, 0, -1),
    ];

    /** 透视相机统一近裁剪面。 */
    public static readonly PERSPECTIVE_CAMERA_NEAR = 0.01;

    /** 透视相机统一远裁剪面。 */
    public static readonly PERSPECTIVE_CAMERA_FAR = 1000;

    /** 正交相机统一近裁剪面。 */
    public static readonly ORTHOGRAPHIC_CAMERA_NEAR = 0.01;

    /** 正交相机统一远裁剪面。 */
    public static readonly ORTHOGRAPHIC_CAMERA_FAR = 1000;


    /**
     * 左下角视口方向导航器的整体 CSS 像素尺寸。
     *
     * 该参数统一控制独立画布、中央立方体、坐标轴与外围按钮的最终屏幕尺寸，
     * 当前值为原 `96px` 的两倍，禁止分别放大内部子对象造成比例漂移。
     */
    public static readonly VIEWPORT_GIZMO_SIZE = 192;


    /** 导航允许的最小距离。 */
    public static readonly CAMERA_CONTROLS_MIN_DISTANCE = 0.01;

    /** 导航允许的最大距离。 */
    public static readonly CAMERA_CONTROLS_MAX_DISTANCE = Number.POSITIVE_INFINITY;

    /** 视图定向器包围余量倍率。 */
    private static readonly VIEW_ORIENTATION_BOUNDING_MARGIN = 1.08;


    /** 空场景下坐标轴不可用时的最小视图定向覆盖边长。 */
    private static readonly VIEW_ORIENTATION_EMPTY_SCENE_MIN_SIZE = 0.05;

    /** 空场景下基于坐标轴计算视图定向覆盖范围时使用的余量倍率。 */
    private static readonly VIEW_ORIENTATION_EMPTY_SCENE_AXIS_MARGIN = 1.2;


    /** 视图定向聚焦相机距离余量倍率。 */
    private static readonly VIEW_ORIENTATION_FOCUS_DISTANCE_MARGIN = 1.2;


    /** 视图定向预览相机默认 FOV。 */
    private static readonly STANDARD_VIEW_PREVIEW_DEFAULT_FOV = 45;

    /** 视图定向预览相机默认宽高比。 */
    private static readonly STANDARD_VIEW_PREVIEW_DEFAULT_ASPECT = 1;

    /** 视图定向预览包围盒留白倍率。 */
    private static readonly STANDARD_VIEW_PREVIEW_BOUNDS_PADDING = 1.15;


    /** 草图缩放覆盖层根对象名称。 */
    private static readonly SKETCH_SCALE_OVERLAY_ROOT_NAME = 'SketchScaleOverlayRoot';

    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** CAD 事件总线操作集合。 */
    private readonly eventBus = useEvent();
    /** 当前活动相机。 */
    public camera: THREE.PerspectiveCamera | THREE.OrthographicCamera
    /** 透视相机。 */
    public perspectiveCamera: THREE.PerspectiveCamera | null = null;
    /** 正交相机。 */
    public orthographicCamera: THREE.OrthographicCamera | null = null;
    /** 当前相机类型。 */
    public cameraType: CameraProjectionType = CameraProjectionType.Orthographic;
    /** 相机控制器。 */
    public controls: CameraControls | null = null;
    /** 轨道控制器绑定的 DOM 元素。 */
    private controlsDomElement: HTMLElement | null = null;
    /** 当前主视口 CSS 像素宽度。 */
    private viewportWidth: number = 1;
    /** 当前主视口 CSS 像素高度。 */
    private viewportHeight: number = 1;
    /** 视口 Gizmo。 */
    public viewportGizmo: ViewportGizmo | null = null;

    /** 视口 Gizmo 外部按钮与拖动使用的纯相机旋转求解器。 */
    private readonly viewportGizmoCameraRotationSolver = new ViewportGizmoCameraRotationSolver();

    /** 视口 Gizmo 标准视图与外部按钮使用的相机动画器。 */
    private viewportGizmoCameraAnimator: ViewportGizmoCameraAnimator | null = null;

    /** 视口 Gizmo 拖动前的默认相机控制器状态。 */
    private viewportGizmoNavigationEnabledBeforeDrag = true;

    /** 应用视口 Gizmo 相机帧时复用的位置缓存。 */
    private readonly viewportGizmoFramePosition = new THREE.Vector3();

    /** 应用视口 Gizmo 相机帧时复用的目标缓存。 */
    private readonly viewportGizmoFrameTarget = new THREE.Vector3();

    /** 应用视口 Gizmo 相机帧时复用的朝上方向缓存。 */
    private readonly viewportGizmoFrameUp = new THREE.Vector3();

    /** 场景内视图定向器。 */
    public viewOrientationMesh: ViewOrientationMesh | null = null;
    /** 视图定向交互控制器。 */
    private viewOrientationInteractionController: ViewOrientationInteractionController | null = null;

    /** 工具栏视图定向预览请求流。 */
    private readonly viewOrientationPreviewRequestSubject = new Subject<StandardViewType | null>();

    /** 鼠标模式。 */
    public mouseMode: MouseMode = MouseMode.Select;

    /** 视图定向聚焦前的相机状态快照。 */
    private viewOrientationFocusSnapshot: {
        position: THREE.Vector3;
        target: THREE.Vector3;
        up: THREE.Vector3;
        projection: CameraProjectionType;
        zoom: number | null;
    } | null = null;

    /** 是否处于视图定向聚焦态。 */
    private viewOrientationFocusActive: boolean = false;
    /** 配置选项。 */
    private readonly options: CameraManagerOptions;

    /** 场景包围盒缓存。 */
    private readonly sceneBounds: THREE.Box3 = new THREE.Box3();

    /** 场景尺寸缓存。 */
    private readonly sceneBoundsSize: THREE.Vector3 = new THREE.Vector3();

    /** 场景中心缓存。 */
    private readonly sceneBoundsCenter: THREE.Vector3 = new THREE.Vector3();


    /** 当前视口可见性判断使用的视锥体缓存。 */
    private readonly viewportFrustum: THREE.Frustum = new THREE.Frustum();

    /** 当前视口可见性判断使用的投影视图矩阵缓存。 */
    private readonly viewportProjectionMatrix: THREE.Matrix4 = new THREE.Matrix4();

    /** 当前视口候选对象包围盒缓存。 */
    private readonly viewportCandidateBounds: THREE.Box3 = new THREE.Box3();

    /** 当前视口候选对象包围盒角点缓存。 */
    private readonly viewportCandidateBoundsCorners: THREE.Vector3[] = Array.from(
        { length: 8 },
        () => new THREE.Vector3(),
    );

    /** 当前真实模型相对屏幕的覆盖状态。 */
    private viewportRotationCoverageState: ViewportRotationCoverageState = ViewportRotationCoverageState.Empty;

    /** 当前视口中心关注旋转中心解析器。 */
    private readonly viewportRotationFocusResolver: ViewportRotationFocusResolver = new ViewportRotationFocusResolver();
    /** 正式单实例空间入口，旋转、全图与裁剪共用同一范围语义。 */
    private readonly modelSpatialQuery: CameraModelSpatialQuery;
    /** 裁剪范围独立于旋转中心/视口范围，仅模型依赖变化时重新聚合。 */
    private readonly clippingBoundsCache: CameraClippingBoundsCache;
    /** 内部裁剪读取缓冲，避免每次 change 克隆 Box3。 */
    private readonly clippingWorldBounds = new THREE.Box3();

    /** 旋转中心拓扑代理高亮控制器。 */
    private readonly rotationCenterTopologyHighlightController: RotationCenterTopologyHighlightController = new RotationCenterTopologyHighlightController();

    /** 当前旋转中心可高亮的拓扑代理目标。 */
    private rotationCenterTopologyHighlightTarget: RotationCenterTopologyHighlightTarget | null = null;

    /** 当前视口中心关注解析器使用的对象过滤适配器。 */
    private readonly shouldParticipateInRotationFocusBounds = (object: THREE.Object3D): boolean => (
        this.shouldParticipateInVisibleItemsBounds(object)
    );

    /** 旋转交互期间锁定的旋转中心。 */
    private readonly lockedRotationCenter: THREE.Vector3 = new THREE.Vector3();

    /** 当前是否已锁定旋转中心，避免旋转过程中因视口内容变化切换包围盒。 */
    private rotationCenterLocked: boolean = false;


    /** 是否显示旋转中心调试辅助对象，默认开启便于直接观察当前旋转中心。 */
    private _showRotationCenterDebug: boolean = true;

    /** 旋转中心调试可视对象。 */
    private rotationCenterDebugVisual: RotationCenterDebugVisual | null = null;


    /** 控制器目标点读取缓存。 */
    private readonly controlsTargetCache: THREE.Vector3 = new THREE.Vector3();


    /** 包围盒相机聚焦求解器。 */
    private readonly boundingBoxCameraFocusSolver: BoundingBoxCameraFocusSolver = new BoundingBoxCameraFocusSolver();


    /** 视图定向包围盒调试计数。 */
    private viewOrientationBoundsDebugIndex: number = 0;


    /** 正交相机动态裁剪包围盒深度缩放系数。 */
    private static readonly ORTHOGRAPHIC_CLIPPING_BOUNDS_DEPTH_SCALE = 1.2;
    /** 正交相机动态裁剪最小单侧深度留白。 */
    private static readonly ORTHOGRAPHIC_CLIPPING_MIN_DEPTH_MARGIN = 0.01;
    /** 正交相机动态裁剪投影点缓存。 */
    private readonly orthographicClippingCornerProjection = new THREE.Vector3();
    /** 正交相机动态裁剪视线方向缓存。 */
    private readonly orthographicClippingViewDirection = new THREE.Vector3();
    /** 正交相机动态裁剪目标点缓存。 */
    private readonly orthographicClippingTarget = new THREE.Vector3();


    /** 已创建正交相机调试面板的 Inspector 实例。 */
    private orthographicCameraDebugPanelInspector: object | null = null;
    /** 正交相机调试面板分组。 */
    private orthographicCameraDebugPanelGroup: CameraDebugParameterGroup | null = null;
    /** 是否显示正交相机调试面板，默认关闭避免常规使用时显示调试 UI。 */
    private _showOrthographicCameraDebugPanel: boolean = false;


    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     * @param options 相机配置选项。
     */
    constructor(viewer: Viewer, options: CameraManagerOptions) {
        this.viewer = viewer;
        this.options = options;
        this.modelSpatialQuery = new CameraModelSpatialQuery(viewer.getImportedModelInstancedUnitSource?.());
        this.clippingBoundsCache = new CameraClippingBoundsCache(this.modelSpatialQuery,
            this.shouldParticipateInRotationFocusBounds, () => viewer.meshManager?.getSceneObjectMembershipSource());
    }

    /**
     * 初始化相机、轨道控制器和视口 Gizmo。
     * @param container 渲染容器元素。
     * @param webGLRenderer WebGL 渲染器实例。
     */
    init(container: HTMLElement, webGLRenderer: WebGPURenderer): void {
        const initialWidth = container.clientWidth;
        const initialHeight = container.clientHeight;
        const hasValidInitialViewport = Number.isFinite(initialWidth)
            && Number.isFinite(initialHeight)
            && initialWidth > 0
            && initialHeight > 0;
        // Viewer 可能在隐藏页签中以 0×0 初始化；先用稳定 1×1 投影，首次有效 resize 再覆盖真实宽高比。
        this.viewportWidth = hasValidInitialViewport ? initialWidth : 1;
        this.viewportHeight = hasValidInitialViewport ? initialHeight : 1;
        const aspect = this.viewportWidth / this.viewportHeight;
        this.controlsDomElement = webGLRenderer.domElement;

        // 创建透视相机
        this.perspectiveCamera = new THREE.PerspectiveCamera(
            this.options.fov,
            aspect,
            CameraManager.PERSPECTIVE_CAMERA_NEAR,
            CameraManager.PERSPECTIVE_CAMERA_FAR,
        );

        this.perspectiveCamera.name = CameraManager.MAIN_PERSPECTIVE_CAMERA_DEBUG_NAME;
        this.perspectiveCamera.position.set(...this.options.cameraPosition);
        this.perspectiveCamera.lookAt(0, 0, 0);
        // 配置主相机显示所有 layer（包括视角定位 mesh 所在的 layer）
        this.perspectiveCamera.layers.enableAll();

        // 创建正交相机
        this.orthographicCamera = this.createOrthographicCamera(
            aspect,
            this.perspectiveCamera.position.clone(),
            new THREE.Vector3(0, 0, 0)
        );
        this.orthographicCamera.name = CameraManager.MAIN_ORTHOGRAPHIC_CAMERA_DEBUG_NAME;
        this.orthographicCamera.layers.enableAll();

        this.camera = this.orthographicCamera;
        this.cameraType = CameraProjectionType.Orthographic;
        // 创建相机控制器
        this.controls = this.createCameraControls(
            this.camera
        );
        // 初始位置/目标同步到 CameraControls 内部状态
        void this.controls.setLookAt(
            this.camera.position.x,
            this.camera.position.y,
            this.camera.position.z,
            0,
            0,
            0,
        );
        this.applyMouseMode();


        // 本地导航器使用独立透明 WebGL 画布，只向 CameraManager 发布强类型相机意图。
        this.viewportGizmo = new ViewportGizmo({
            container,
            size: CameraManager.VIEWPORT_GIZMO_SIZE,
            pixelRatio: container.ownerDocument.defaultView?.devicePixelRatio ?? 1,
        });
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.StandardViewSelected,
            this.handleViewportGizmoStandardViewSelected,
        );
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.ControlActionTriggered,
            this.handleViewportGizmoControlActionTriggered,
        );
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.OrbitDragStarted,
            this.handleViewportGizmoOrbitDragStarted,
        );
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.OrbitDragChanged,
            this.handleViewportGizmoOrbitDragChanged,
        );
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.OrbitDragEnded,
            this.handleViewportGizmoOrbitDragEnded,
        );
        this.viewportGizmo.addEventListener(
            ViewportGizmoEventType.HoverChanged,
            this.handleViewportGizmoVisualChanged,
        );
        this.viewportGizmo.start();
        this.viewportGizmoCameraAnimator = new ViewportGizmoCameraAnimator({
            getCamera: () => this.camera,
            getTarget: () => this.controls?.target ?? this.controlsTargetCache,
            isNavigationEnabled: () => this.controls?.enabled ?? false,
            setNavigationEnabled: (enabled) => {
                if (this.controls) {
                    this.controls.enabled = enabled;
                }
            },
            syncNavigation: this.syncViewportGizmoNavigationState,
        });


        this.controls.addEventListener('start', this.handleCameraControlsInteractionStart);
        this.controls.addEventListener('end', this.handleCameraControlsInteractionEnd);

        this.controls.addEventListener('change', this.handleCameraControlsUpdate);

        this.viewOrientationMesh = new ViewOrientationMesh();
        this.viewOrientationMesh.visible = false;
        this.viewer.addObject(this.viewOrientationMesh);
        this.viewOrientationMesh.position.set(0, 0, 0);
        this.viewOrientationMesh.updateMatrixWorld();
        this.dispatchCameraProjectionTypeChanged();
        this.dispatchCameraStateChanged();


        this.viewer.scene.addEventListener(
            OrthographicClippingSceneEventType.ChildAdded,
            this.handleSceneContentChangedForOrthographicClipping,
        );
        this.viewer.scene.addEventListener(
            OrthographicClippingSceneEventType.ChildRemoved,
            this.handleSceneContentChangedForOrthographicClipping,
        );
        this.syncOrthographicCameraClippingFromVisibleBounds();


        // 默认关闭正交相机调试面板，避免常规 Viewer 初始化时挂载调试 UI。
        if (this._showOrthographicCameraDebugPanel) {
            this.createOrthographicCameraDebugPanel();
        }


    }

    /**
     * 刷新相机相关的每帧状态。
     *
     * 设计说明：
     * 1. 每个宿主动画帧均推进 controls 阻尼和导航器动画，不依赖本帧是否绘制；
     * 2. 相机变化只登记重绘，主场景与导航器在统一提交阶段绘制；
     * 3. 同步出口也复用此准备阶段，render(false) 避免重复消耗同帧阻尼。
     */
    public updateFrame(): void {
        // CameraControls 先消费自身阻尼，再由导航器动画写入最终帧，避免旧阻尼覆盖动画结果。
        this.controls.update();
        const wasAnimating = this.viewportGizmoCameraAnimator?.isAnimating === true;
        this.viewportGizmoCameraAnimator?.update();
        // 最后一帧也必须登记：update 结束后 isAnimating 已可能变为 false。
        if (wasAnimating) this.viewer.requestRender();
    }

    /**
     * 绘制相机辅助视图；默认保留直接调用 render 时先更新相机状态的行为。
     * @param updateCameraState Viewer 帧调度已经推进状态时传 false，避免重复消耗阻尼。
     */
    render(updateCameraState: boolean = true): void {
        if (updateCameraState) this.updateFrame();
        this.syncViewOrientationMeshState();
        this.viewportGizmo?.render(this.camera, this.getCurrentStandardViewType());

    }


    /**
     * 是否显示旋转中心调试辅助对象。
     *
     * 设计说明：
     * 1. 该属性只控制调试可视对象，不改变相机、控制器或模型数据；
     * 2. 开启时只允许后续旋转会话显示中心标识，非旋转状态不常驻显示；
     * 3. 默认开启，关闭时只隐藏辅助对象，资源留到 `dispose()` 统一释放，避免频繁开关反复创建材质和几何体。
     */
    public get showRotationCenterDebug(): boolean {
        return this.isRotationCenterDebugVisible();
    }

    /**
     * 设置是否显示旋转中心调试辅助对象。
     * @param visible 是否显示旋转中心调试辅助对象。
     */
    public set showRotationCenterDebug(visible: boolean) {
        this.setRotationCenterDebugVisible(visible);
    }

    /**
     * 设置旋转中心调试辅助对象显隐。
     *
     * @param visible 是否显示旋转中心调试辅助对象。
     */
    public setRotationCenterDebugVisible(visible: boolean): void {
        this._showRotationCenterDebug = visible;

        if (visible) {
            // 开启调试只恢复旋转会话内的中心标识显示能力，非旋转状态仍保持隐藏，避免常驻遮挡模型。
            this.refreshRotationCenterDebugVisuals();
        } else {
            // 关闭时隐藏根对象即可，已创建资源由 dispose 统一释放，避免调试开关造成抖动分配。
            this.rotationCenterTopologyHighlightController.clear();
            this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.Hidden);
        }

        this.viewer.requestRender();
    }

    /**
     * 读取旋转中心调试辅助对象显隐状态。
     *
     * @returns 是否显示旋转中心调试辅助对象。
     */
    public isRotationCenterDebugVisible(): boolean {
        return this._showRotationCenterDebug;
    }


    /**
     * 是否显示正交相机调试面板。
     *
     * 说明：
     * 1. 默认关闭；
     * 2. 该属性只控制正交相机调试面板，不控制 Inspector 整体显示；
     * 3. 设置为 `true` 时，如果初始化条件已经满足，会自动创建并显示面板；
     * 4. 设置为 `false` 时，只隐藏已有面板，不移除 Inspector。
     */
    public get showOrthographicCameraDebugPanel(): boolean {
        return this._showOrthographicCameraDebugPanel;
    }

    /**
     * 设置是否显示正交相机调试面板。
     * @param visible 是否显示。
     */
    public set showOrthographicCameraDebugPanel(visible: boolean) {
        this._showOrthographicCameraDebugPanel = visible;
        if (visible && this.orthographicCamera && this.viewer.renderer) {
            this.createOrthographicCameraDebugPanel();
            return;
        }

        this.syncOrthographicCameraDebugPanelVisibility();
    }

    /**
     * 创建正交相机调试面板。
     *
     * 说明：
     * 1. 面板通过 Viewer 的 WebGPU Inspector 创建，调用方无需直接接触 renderer；
     * 2. 调试属性使用 getter/setter 写入正交相机，保证每次修改都能统一刷新投影矩阵与视口；
     * 3. 同一个 Inspector 上重复调用只创建一次面板，避免参数项重复堆叠；
     * 4. 面板创建与显隐分离，`showOrthographicCameraDebugPanel` 只控制 UI 是否显示。
     *
     * @throws 当正交相机尚未初始化时抛出错误。
     */
    public createOrthographicCameraDebugPanel(): void {
        const inspector = this.viewer.createInspector();
        if (this.orthographicCameraDebugPanelInspector === inspector) {
            this.syncOrthographicCameraDebugPanelVisibility();
            return;
        }

        if (!this.orthographicCamera) {
            throw new Error('创建正交相机调试面板失败：正交相机尚未初始化。');
        }

        const rootGroup = inspector.createParameters('正交相机调试') as CameraDebugParameterGroup;
        this.orthographicCameraDebugPanelGroup = rootGroup;
        const frustumModel = this.createOrthographicCameraDebugModel({
            left: () => this.orthographicCamera!.left,
            right: () => this.orthographicCamera!.right,
            top: () => this.orthographicCamera!.top,
            bottom: () => this.orthographicCamera!.bottom,
            near: () => this.orthographicCamera!.near,
            far: () => this.orthographicCamera!.far,
            zoom: () => this.orthographicCamera!.zoom,
        }, {
            left: (value) => { this.orthographicCamera!.left = value; },
            right: (value) => { this.orthographicCamera!.right = value; },
            top: (value) => { this.orthographicCamera!.top = value; },
            bottom: (value) => { this.orthographicCamera!.bottom = value; },
            near: (value) => { this.orthographicCamera!.near = value; },
            far: (value) => { this.orthographicCamera!.far = value; },
            zoom: (value) => { this.orthographicCamera!.zoom = Math.max(value, Number.EPSILON); },
        });
        const positionModel = this.createOrthographicCameraDebugModel({
            x: () => this.orthographicCamera!.position.x,
            y: () => this.orthographicCamera!.position.y,
            z: () => this.orthographicCamera!.position.z,
        }, {
            x: (value) => { this.orthographicCamera!.position.x = value; },
            y: (value) => { this.orthographicCamera!.position.y = value; },
            z: (value) => { this.orthographicCamera!.position.z = value; },
        });
        const rotationModel = this.createOrthographicCameraDebugModel({
            x: () => this.orthographicCamera!.rotation.x,
            y: () => this.orthographicCamera!.rotation.y,
            z: () => this.orthographicCamera!.rotation.z,
        }, {
            x: (value) => { this.orthographicCamera!.rotation.x = value; },
            y: (value) => { this.orthographicCamera!.rotation.y = value; },
            z: (value) => { this.orthographicCamera!.rotation.z = value; },
        });
        const upModel = this.createOrthographicCameraDebugModel({
            x: () => this.orthographicCamera!.up.x,
            y: () => this.orthographicCamera!.up.y,
            z: () => this.orthographicCamera!.up.z,
        }, {
            x: (value) => { this.orthographicCamera!.up.x = value; },
            y: (value) => { this.orthographicCamera!.up.y = value; },
            z: (value) => { this.orthographicCamera!.up.z = value; },
        });

        this.addOrthographicCameraDebugFields(rootGroup.addFolder('视锥体'), frustumModel, [
            ['left', 'left'],
            ['right', 'right'],
            ['top', 'top'],
            ['bottom', 'bottom'],
            ['near', 'near'],
            ['far', 'far'],
            ['zoom', 'zoom'],
        ]);
        this.addOrthographicCameraDebugFields(rootGroup.addFolder('位置'), positionModel, [
            ['x', 'position.x'],
            ['y', 'position.y'],
            ['z', 'position.z'],
        ]);
        this.addOrthographicCameraDebugFields(rootGroup.addFolder('旋转'), rotationModel, [
            ['x', 'rotation.x'],
            ['y', 'rotation.y'],
            ['z', 'rotation.z'],
        ]);
        this.addOrthographicCameraDebugFields(rootGroup.addFolder('上方向'), upModel, [
            ['x', 'up.x'],
            ['y', 'up.y'],
            ['z', 'up.z'],
        ]);
        this.addOrthographicCameraDebugActions(rootGroup.addFolder('操作'));

        this.orthographicCameraDebugPanelInspector = inspector;
        this.syncOrthographicCameraDebugPanelVisibility();
    }

    /**
     * 添加正交相机调试操作按钮。
     * @param group Inspector 参数分组。
     */
    private addOrthographicCameraDebugActions(group: CameraDebugParameterGroup): void {
        const actionModel: Record<string, unknown> = {
            printFrustumState: () => {
                this.printOrthographicCameraDebugState();
            },
        };

        group.add(actionModel, 'printFrustumState').name('打印当前视锥体状态');
    }

    /**
     * 创建正交相机调试参数模型。
     * @param getters 参数读取函数集合。
     * @param setters 参数写入函数集合。
     * @returns 可供 Inspector 绑定的参数对象。
     */
    private createOrthographicCameraDebugModel(
        getters: Record<string, () => number>,
        setters: Record<string, (value: number) => void>,
    ): Record<string, unknown> {
        const model: Record<string, unknown> = {};

        Object.keys(getters).forEach((key) => {
            Object.defineProperty(model, key, {
                enumerable: true,
                get: () => getters[key](),
                set: (value: number) => {
                    setters[key](value);
                    this.refreshOrthographicCameraDebugState();
                },
            });
        });

        return model;
    }

    /**
     * 给调试参数分组批量添加字段。
     * @param group Inspector 参数分组。
     * @param model 参数模型。
     * @param fields 字段配置。
     */
    private addOrthographicCameraDebugFields(
        group: CameraDebugParameterGroup,
        model: Record<string, unknown>,
        fields: Array<[property: string, displayName: string]>,
    ): void {
        fields.forEach(([property, displayName]) => {
            group.add(model, property).name(displayName).listen();
        });
    }

    /**
     * 获取当前正交相机调试状态。
     * @returns 当前正交相机视锥体、位置、旋转与上方向状态。
     */
    private getOrthographicCameraDebugState(): OrthographicCameraDebugState {
        if (!this.orthographicCamera) {
            throw new Error('获取正交相机调试状态失败：正交相机尚未初始化。');
        }

        const {left, right, top, bottom, near, far, zoom, position, rotation, up} = this.orthographicCamera;

        return {
            left,
            right,
            top,
            bottom,
            near,
            far,
            zoom,
            position: {
                x: position.x,
                y: position.y,
                z: position.z,
            },
            rotation: {
                x: rotation.x,
                y: rotation.y,
                z: rotation.z,
            },
            up: {
                x: up.x,
                y: up.y,
                z: up.z,
            },
        };
    }

    /**
     * 打印当前正交相机调试状态。
     */
    private printOrthographicCameraDebugState(): void {
        console.info('正交相机当前视锥体状态', this.getOrthographicCameraDebugState());
    }

    /**
     * 刷新正交相机调试修改后的渲染状态。
     */
    private refreshOrthographicCameraDebugState(): void {
        if (!this.orthographicCamera) {
            return;
        }

        this.orthographicCamera.updateProjectionMatrix();
        this.orthographicCamera.updateMatrixWorld(true);

        if (this.cameraType === CameraProjectionType.Orthographic) {
            this.camera = this.orthographicCamera;
        }

        this.dispatchCameraStateChanged();
        this.viewer.requestRender();
    }

    /**
     * 同步正交相机调试面板显隐状态。
     */
    private syncOrthographicCameraDebugPanelVisibility(): void {
        if (!this.orthographicCameraDebugPanelGroup) {
            return;
        }

        this.orthographicCameraDebugPanelGroup.paramList.domElement.style.display = this._showOrthographicCameraDebugPanel
            ? ''
            : 'none';
    }


    /**
     * 重置相机到初始位置。
     */
    resetCamera(): void {
        if (!this.camera || !this.controls) {
            return;
        }

        this.syncCameraTransforms(
            new THREE.Vector3(...this.options.cameraPosition),
            new THREE.Vector3(0, 0, 0),
            this.camera.up.clone(),
        );

        this.controls.update();
    }


    /**
     * 读取当前相机投影类型。
     *
     * @returns 当前相机投影类型。
     */
    public getCameraProjectionType(): CameraProjectionType {
        return this.cameraType === CameraProjectionType.Orthographic
            ? CameraProjectionType.Orthographic
            : CameraProjectionType.Perspective;
    }

    /**
     * 切换相机投影类型。
     *
     * 设计说明：
     * 1. 正交构图由 zoom/frustum 决定，几何距离可能被动态裁剪推远且不影响画面大小；
     * 2. 透视构图完全依赖几何距离与 FOV，切换时必须做等价换算，避免模型突然变小或变大；
     * 3. 换算只调整投影语义相关参数，不改变 target、up 与当前视线方向。
     *
     * @param projectionType 目标投影类型。
     */
    public setCameraProjectionType(projectionType: CameraProjectionType): void {
        if (!this.camera) {
            return;
        }
        if (this.cameraType === projectionType) {
            return;
        }


        // 投影切换会替换活动相机和 CameraControls，必须先终止仍绑定旧相机的导航器动画。
        this.viewportGizmoCameraAnimator?.cancel();


        const nextCamera = projectionType === CameraProjectionType.Orthographic
            ? this.orthographicCamera
            : this.perspectiveCamera;
        if (!nextCamera) {
            return;
        }

        const target = this.getControlsTarget();
        let position = this.camera.position.clone();
        const up = this.camera.up.clone();
        const controlsEnabled = this.controls?.enabled ?? true;


        let orthographicEquivalentZoom: number | null = null;

        if (
            projectionType === CameraProjectionType.Perspective
            && this.camera instanceof THREE.OrthographicCamera
            && this.perspectiveCamera
        ) {
            const viewDirection = position.clone().sub(target);
            if (viewDirection.lengthSq() <= Number.EPSILON) {
                viewDirection.set(1, 1, 1);
            }
            viewDirection.normalize();

            const equivalentDistance = CameraProjectionDistanceConverter.resolvePerspectiveEquivalentDistance({
                orthographicCamera: this.camera,
                perspectiveFov: this.perspectiveCamera.fov,
            });
            position = target.clone().add(viewDirection.multiplyScalar(equivalentDistance));
        }

        if (
            projectionType === CameraProjectionType.Orthographic
            && this.camera instanceof THREE.PerspectiveCamera
            && this.orthographicCamera
        ) {
            orthographicEquivalentZoom = CameraProjectionDistanceConverter.resolveOrthographicEquivalentZoom({
                perspectiveCamera: this.camera,
                orthographicFrustumHeight: Math.abs(this.orthographicCamera.top - this.orthographicCamera.bottom),
                target,
            });
        }


        this.cameraType = projectionType;
        this.camera = nextCamera;
        this.syncCameraTransforms(position, target, up);

        if (orthographicEquivalentZoom !== null && this.orthographicCamera) {
            this.orthographicCamera.zoom = orthographicEquivalentZoom;
            this.orthographicCamera.updateProjectionMatrix();
        }

        this.recreateCameraControls(controlsEnabled, target);
        this.dispatchCameraProjectionTypeChanged();
        this.dispatchCameraStateChanged();

        this.syncOrthographicCameraClippingFromVisibleBounds();

        this.viewer.requestRender();
    }

    /**
     * 派发相机投影类型变化事件。
     *
     * 设计说明：
     * 1. 相机切换属于视图状态变化，不直接依赖具体材质实现；
     * 2. 通过事件总线广播投影状态，让点材质等渲染模块自行同步 uniform；
     * 3. 事件载荷只包含稳定协议字段，避免外部持有相机实例导致生命周期耦合。
     */
    private dispatchCameraProjectionTypeChanged(): void {
        const payload: CameraProjectionTypeChangedEventPayload = {
            projectionType: this.cameraType,
            isPerspectiveCamera: this.cameraType === CameraProjectionType.Perspective,
        };

        this.eventBus.dispatch(CameraEventName.ProjectionTypeChanged, payload);
    }

    /**
     * 派发当前活动相机及视口状态。
     *
     * 基准面文字等屏幕尺寸敏感对象直接订阅该事件完成自身更新，CameraManager
     * 不持有这些显示对象，显示对象也不需要通过 provider 反向读取相机或容器。
     */
    private dispatchCameraStateChanged(): void {
        if (!this.camera) {
            return;
        }

        const payload: CameraStateChangedEventPayload = {
            camera: this.camera,
            viewportWidth: this.viewportWidth,
            viewportHeight: this.viewportHeight,
        };
        this.eventBus.dispatch(CameraEventName.StateChanged, payload);
        this.viewer.requestRender();
    }

    /**
     * 读取当前标准视图的相机帧。
     *
     * 设计说明：
     * 1. `currentCameraUp` 为可选参数，传入时会将其正交投影到目标视图方向的垂直平面，
     *    以确保预览 / 切换后的视角 up 与当前旋转姿态一致；
     * 2. 投影结果近零（up 与视图方向平行，如正视 Top 面）时 fallback 到定义表固定 up，
     *    避免产生零向量，行为与 SolidWorks 一致；
     * 3. 不传入时完全沿用旧行为，保持调用兼容性。
     *
     * @param viewType 标准视图类型。
     * @param currentCameraUp 当前相机 up 向量（可选）；传入时用于推导感知一致的 up。
     * @param referencePerspectiveFov 参考透视 FOV（度）；正交模式下用于推导等效观察距离，预览画布应传入预览相机 FOV。
     * @returns 相机帧；当相机或控制器未就绪时返回 null。
     */
    public buildStandardViewCameraFrame(
        viewType: StandardViewType,
        currentCameraUp?: THREE.Vector3,
        referencePerspectiveFov?: number,
    ): StandardViewCameraFrame | null {
        if (!this.camera || !this.controls) {
            return null;
        }

        const target = this.getControlsTarget();
        const distance = this.resolveStandardViewDistance(target, referencePerspectiveFov);

        // 先用静态定义表构建基础帧（position / target / 默认 up）
        const frame = buildStandardViewCameraFrame({
            viewType,
            target: [target.x, target.y, target.z],
            distance,
        });

        return this.applyCurrentCameraUpToStandardViewFrame(frame, currentCameraUp);
    }


    /**
     * 构建右上角标准视图预览相机帧。
     *
     * 设计说明：
     * 1. 点击标准视图必须保持主视口当前缩放层级，所以 `buildStandardViewCameraFrame()` 继续读取主相机距离；
     * 2. 右上角预览的职责是“看清即将切换的模型姿态”，不能被主相机物理距离或正交 zoom 表达方式限制；
     * 3. 因此预览帧独立按可见模型包围盒、预览相机 FOV 与宽高比计算距离，毫米级模型也能占满预览画布。
     *
     * @param viewType 标准视图类型。
     * @param currentCameraUp 当前相机 up 向量（可选）；用于保持预览和点击后的屏幕朝上方向一致。
     * @param options 预览相机参数。
     * @returns 预览相机帧；相机或控制器未就绪时返回 null。
     */
    public buildStandardViewPreviewCameraFrame(
        viewType: StandardViewType,
        currentCameraUp?: THREE.Vector3,
        options: StandardViewPreviewFrameOptions = {},
    ): StandardViewCameraFrame | null {
        if (!this.camera || !this.controls) {
            return null;
        }

        const bounds = this.resolveVisibleItemsBounds();
        if (!bounds) {
            return this.buildStandardViewCameraFrame(
                viewType,
                currentCameraUp,
                options.fov ?? CameraManager.STANDARD_VIEW_PREVIEW_DEFAULT_FOV,
            );
        }

        const target = bounds.getCenter(new THREE.Vector3());
        const seedFrame = buildStandardViewCameraFrame({
            viewType,
            target: [target.x, target.y, target.z],
            distance: 1,
        });
        const orientedSeedFrame = this.applyCurrentCameraUpToStandardViewFrame(seedFrame, currentCameraUp);
        const distance = this.resolveStandardViewPreviewFitDistance(bounds, orientedSeedFrame, options);
        const frame = buildStandardViewCameraFrame({
            viewType,
            target: [target.x, target.y, target.z],
            distance,
        });

        return this.applyCurrentCameraUpToStandardViewFrame(frame, currentCameraUp);
    }

    /**
     * 根据可见模型包围盒解析右上角预览所需的透视相机距离。
     *
     * @param bounds 可见模型包围盒。
     * @param frame 方向和 up 已确定的标准视图种子帧。
     * @param options 预览相机参数。
     * @returns 能覆盖包围盒的预览相机距离。
     */
    private resolveStandardViewPreviewFitDistance(
        bounds: THREE.Box3,
        frame: StandardViewCameraFrame,
        options: StandardViewPreviewFrameOptions,
    ): number {
        const target = new THREE.Vector3(...frame.target);
        const viewDirection = new THREE.Vector3(...frame.position).sub(target);
        if (viewDirection.lengthSq() <= Number.EPSILON) {
            return 1;
        }
        viewDirection.normalize();

        const up = new THREE.Vector3(...frame.up).normalize();
        const right = viewDirection.clone().cross(up);
        if (right.lengthSq() <= Number.EPSILON) {
            return 1;
        }
        right.normalize();
        const actualUp = right.clone().cross(viewDirection).normalize();
        const fov = THREE.MathUtils.clamp(
            options.fov ?? CameraManager.STANDARD_VIEW_PREVIEW_DEFAULT_FOV,
            1,
            175,
        );
        const aspect = Math.max(options.aspect ?? CameraManager.STANDARD_VIEW_PREVIEW_DEFAULT_ASPECT, Number.EPSILON);
        const halfTanVertical = Math.tan(THREE.MathUtils.degToRad(fov / 2));
        const halfTanHorizontal = halfTanVertical * aspect;
        const corners = CameraManager.collectBoxCorners(bounds);
        let requiredDistance = Number.EPSILON;

        corners.forEach((corner) => {
            // 按预览相机局部坐标投影包围盒角点，距离必须同时覆盖横向、纵向和朝相机方向的深度。
            const relative = corner.clone().sub(target);
            const horizontal = Math.abs(relative.dot(right)) * CameraManager.STANDARD_VIEW_PREVIEW_BOUNDS_PADDING;
            const vertical = Math.abs(relative.dot(actualUp)) * CameraManager.STANDARD_VIEW_PREVIEW_BOUNDS_PADDING;
            const depth = relative.dot(viewDirection);
            requiredDistance = Math.max(
                requiredDistance,
                depth + horizontal / halfTanHorizontal,
                depth + vertical / halfTanVertical,
            );
        });

        return Math.max(requiredDistance, Number.EPSILON);
    }

    /**
     * 基于当前相机 up 修正标准视图相机帧。
     *
     * @param frame 原始标准视图相机帧。
     * @param currentCameraUp 当前相机 up 向量（可选）。
     * @returns 修正后的相机帧。
     */
    private applyCurrentCameraUpToStandardViewFrame(
        frame: StandardViewCameraFrame,
        currentCameraUp?: THREE.Vector3,
    ): StandardViewCameraFrame {
        if (!currentCameraUp) {
            return frame;
        }

        // 目标视图方向（从目标点指向相机位置）
        const viewDir = new THREE.Vector3(...frame.position)
            .sub(new THREE.Vector3(...frame.target))
            .normalize();

        // 将当前相机 up 投影到垂直于目标视图方向的平面，作为方向参考
        const upNorm = currentCameraUp.clone().normalize();
        const projected = upNorm
            .sub(viewDir.clone().multiplyScalar(upNorm.dot(viewDir)));

        if (projected.lengthSq() <= 1e-10) {
            // 投影近零（up 平行于视图方向），fallback 到定义表固定 up。
            return frame;
        }

        projected.normalize();

        // 从6个世界轴候选中找与参考方向最对齐的，保证 up 变化为 90° 整数倍。
        // 原理：candidate.dot(projected) 中 projected 已无 viewDir 分量，
        // 因此即使 candidate 不完全垂直于 viewDir，点积结果也只反映垂直分量对齐度，计算正确。
        let bestDot = -Infinity;
        let bestUp: StandardViewCameraFrame['up'] = frame.up;

        for (const candidate of CameraManager.CANDIDATE_UP_AXES) {
            // 跳过与视图方向近平行的轴（平行轴无法作为有效 up）
            if (Math.abs(candidate.dot(viewDir)) > 1 - 1e-6) {
                continue;
            }

            const alignment = candidate.dot(projected);
            if (alignment > bestDot) {
                bestDot = alignment;
                bestUp = [candidate.x, candidate.y, candidate.z];
            }
        }

        return { ...frame, up: bestUp };
    }


    /**
     * 解析标准视图应使用的观察距离。
     *
     * 设计说明：
     * 1. 透视相机可直接使用 position-target 几何距离；
     * 2. 正交相机的几何距离可能被动态裁剪推远，与屏幕构图无关，必须换算为等效透视距离；
     * 3. 预览画布使用固定 FOV 的透视相机，因此允许传入 referencePerspectiveFov 与预览保持一致。
     *
     * @param target 当前观察目标。
     * @param referencePerspectiveFov 参考透视 FOV（度）；未传时使用主透视相机 FOV。
     * @returns 标准视图观察距离。
     */
    private resolveStandardViewDistance(
        target: THREE.Vector3,
        referencePerspectiveFov?: number,
    ): number {
        if (!this.camera) {
            return 1;
        }

        if (this.camera instanceof THREE.PerspectiveCamera) {
            return Math.max(this.camera.position.distanceTo(target), Number.EPSILON);
        }

        if (this.camera instanceof THREE.OrthographicCamera) {
            const perspectiveFov = referencePerspectiveFov
                ?? this.perspectiveCamera?.fov
                ?? this.options.fov;

            return CameraProjectionDistanceConverter.resolvePerspectiveEquivalentDistance({
                orthographicCamera: this.camera,
                perspectiveFov,
            });
        }

        // 类型层面 camera 仅可能为透视/正交，此处保留防御分支供未来扩展其它 Camera 子类。
        return Math.max(
            (this.camera as THREE.PerspectiveCamera).position.distanceTo(target),
            Number.EPSILON,
        );
    }

    /**
     * 读取当前标准视图类型。
     *
     * @returns 当前标准视图类型；未匹配时返回 null。
     */
    public getCurrentStandardViewType(): StandardViewType | null {
        if (!this.camera || !this.controls) {
            return null;
        }

        const direction = this.camera.position.clone().sub(this.getControlsTarget());
        if (direction.lengthSq() <= Number.EPSILON) {
            return null;
        }

        direction.normalize();
        const currentUp = this.camera.up.clone().normalize();
        const directionTolerance = 0.9998;
        const upTolerance = 0.9998;

        for (const definition of listStandardViewDefinitions()) {
            const definitionDirection = new THREE.Vector3(...definition.direction).normalize();
            const definitionUp = new THREE.Vector3(...definition.up).normalize();

            if (direction.dot(definitionDirection) >= directionTolerance && currentUp.dot(definitionUp) >= upTolerance) {
                return definition.type;
            }
        }

        return null;
    }

    /**
     * 按标准视图切换相机。
     *
     * 设计说明：
     * 传入当前相机 up，确保点击后的主视角 up 与 hover 预览保持完全一致，
     * 避免点击后产生与预览不符的视角闪动。
     *
     * @param viewType 标准视图类型。
     */
    public setStandardView(viewType: StandardViewType): void {
        // 传入当前相机 up，与预览保持一致
        const frame = this.buildStandardViewCameraFrame(viewType, this.camera.up.clone());
        if (!frame) {
            return;
        }

        this.lookAtTarget(
            new THREE.Vector3(...frame.position),
            new THREE.Vector3(...frame.target),
            new THREE.Vector3(...frame.up),
        );
        this.viewOrientationMesh?.setActiveViewType(viewType);
        this.controls?.update();
        this.viewer.requestRender();
    }

    /**
     * 启动视图定向交互控制器。
     */
    public startViewOrientationInteraction(): void {
        if (this.viewOrientationInteractionController) {
            return;
        }

        this.viewOrientationInteractionController = new ViewOrientationInteractionController(this.viewer, this);
        this.viewOrientationInteractionController.start();
        this.syncViewOrientationMeshState();
    }


    /**
     * 读取视图定向器 hover 变更事件流。
     *
     * @returns Observable<StandardViewType | null>；合并场景内定向器 hover 与工具栏预览请求。
     */
    public observeViewOrientationHover(): Observable<StandardViewType | null> {
        const interactionHoverStream = this.viewOrientationInteractionController?.observeHoverChange() ?? EMPTY;
        return merge(interactionHoverStream, this.viewOrientationPreviewRequestSubject.asObservable());
    }


    /**
     * 请求显示或关闭视图定向预览。
     *
     * 说明：
     * 1. 场景内视图定向器 hover 和工具栏下拉项 hover 都应复用同一个右上角预览渲染器；
     * 2. web-client 只声明目标标准视图，不直接持有预览浮层或渲染器；
     * 3. 传入 null 时关闭右上角预览，避免下拉项离开或点击后残留画面。
     *
     * @param viewType 目标标准视图；为 null 时关闭预览。
     */
    public setViewOrientationPreview(viewType: StandardViewType | null): void {
        this.viewOrientationPreviewRequestSubject.next(viewType);
    }


    /**
     * 设置场景内视图定向器可见性。
     *
     * @param visible 是否显示。
     */
    public setViewOrientationMeshVisible(visible: boolean): void {
        if (!this.viewOrientationMesh) {
            return;
        }
        this.viewOrientationMesh.visible = visible;

        // 视图定向器尺寸只在显示瞬间计算一次，避免 render 循环持续遍历场景造成性能和日志噪声。
        if (visible) {
            this.syncViewOrientationMeshScale();
        }

        // 场景内定向器关闭时彻底解除原始指针订阅，避免普通草图拖动进入导航器控制器。
        this.viewOrientationInteractionController?.setPointerInteractionEnabled(visible);

        if (!visible) {
            this.viewOrientationMesh.setHoveredViewType(null);
        }
        this.viewer.requestRender();
    }

    /**
     * 进入视图定向聚焦态。
     *
     * 设计说明：
     * 1. 进入时先保存旧相机状态，供空白点击恢复；
     * 2. 然后显示定向器并把相机调整到可完整观察整个定向器；
     * 3. 多次进入不会重复覆盖快照，避免恢复目标漂移。
     */
    public enterViewOrientationFocusMode(): void {
        if (!this.camera || !this.controls || !this.viewOrientationMesh) {
            return;
        }

        if (!this.viewOrientationFocusActive) {
            this.viewOrientationFocusSnapshot = {
                position: this.camera.position.clone(),
                target: this.getControlsTarget(),
                up: this.camera.up.clone(),
                projection: this.getCameraProjectionType(),
                zoom: this.camera instanceof THREE.OrthographicCamera ? this.camera.zoom : null,
            };
        }

        this.viewOrientationFocusActive = true;
        this.setViewOrientationMeshVisible(true);
        this.focusCameraToViewOrientationMesh();
    }

    /**
     * 退出视图定向聚焦态。
     *
     * @param options 退出选项。
     * @param options.restoreCamera 是否恢复到进入聚焦前的相机状态。
     */
    public exitViewOrientationFocusMode(options?: { restoreCamera?: boolean }): void {
        const restoreCamera = options?.restoreCamera ?? true;
        const snapshot = this.viewOrientationFocusSnapshot;

        if (restoreCamera && snapshot && this.controls) {
            this.setCameraProjectionType(snapshot.projection);
            this.lookAtTarget(
                snapshot.position.clone(),
                snapshot.target.clone(),
                snapshot.up.clone(),
            );
            if (this.camera instanceof THREE.OrthographicCamera && snapshot.zoom !== null) {
                this.camera.zoom = snapshot.zoom;
                this.camera.updateProjectionMatrix();
            }
            void this.controls.setTarget(snapshot.target.x, snapshot.target.y, snapshot.target.z, );
            this.controls.update();
        }

        this.viewOrientationFocusActive = false;
        this.viewOrientationFocusSnapshot = null;
        this.setViewOrientationMeshVisible(false);
    }

    /**
     * 判断是否处于视图定向聚焦态。
     *
     * @returns 是否处于聚焦态。
     */
    public isViewOrientationFocusModeActive(): boolean {
        return this.viewOrientationFocusActive;
    }

    /**
     * 读取场景内视图定向器可见性。
     *
     * @returns 是否显示。
     */
    public isViewOrientationMeshVisible(): boolean {
        return this.viewOrientationMesh?.visible === true;
    }


    /**
     * 启用/禁用轨道控制器交互。
     * @param enabled 是否启用。
     */
    setControlsEnabled(enabled: boolean): void {
        if (this.controls) {
            this.controls.enabled = enabled;
        }
    }

    /**
     * 取消当前相机控制器的指针交互，但不改变相机位姿。
     *
     * 设计说明：
     * 零部件拖拽会接管原本属于相机控制器的输入，同一轮 pointerdown 可能已经进入
     * CameraControls 内部状态机；接管前后必须显式清理该状态，避免释放鼠标后残留交互状态。
     */
    cancelControlsInteraction(): void {
        this.controls?.cancelActiveInteraction();
    }

    /**
     * 根据当前鼠标模式配置控制器按钮映射。
     */
    applyMouseMode(): void {
        if (!this.controls) {
            return;
        }

        // 交互映射说明：
        // 1. 左键：始终让位给选择链路，负责点击选择与拖动框选；
        // 2. 中键：缩放；
        // 3. 右键：普通时旋转，Ctrl+右键时平移；
        // 4. 滚轮：缩放。
        this.configureArcballMouseActions(this.controls);

        this.controls.update();
    }

    /**
     * 处理窗口尺寸变化，更新相机投影矩阵和视口 Gizmo。
     * @param width 新视口宽度。
     * @param height 新视口高度。
     */
    resize(width: number, height: number): void {
        // DOM 在隐藏、卸载或布局过渡期间可能短暂上报零尺寸。此时继续计算投影会把
        // 透视宽高比和正交视锥写成 NaN/Infinity，因此保留最后一次有效视口与投影。
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            return;
        }

        this.viewportWidth = width;
        this.viewportHeight = height;
        if (this.perspectiveCamera) {
            this.perspectiveCamera.aspect = width / height;
            this.perspectiveCamera.updateProjectionMatrix();
        }
        if (this.orthographicCamera) {
            this.updateOrthographicCameraFrustum(width, height);
        }

        this.syncOrthographicCameraClippingFromVisibleBounds();


        // CameraControls 的光标反投影依赖 DOM 屏幕区域，必须跟随 Viewer resize 同步刷新。
        this.controls?.handleResize();

        this.dispatchCameraStateChanged();
    }

    /**
     * 将相机移动到指定位置并朝向目标点。
     * @param position 相机目标位置。
     * @param target 观察目标点。
     * @param up
     */
    lookAtTarget(position: THREE.Vector3, target: THREE.Vector3, up?: THREE.Vector3): void {
        if (!this.camera || !this.controls) {
            return;
        }

        // 任何外部直接定位都应中断导航器动画，避免下一帧动画重新覆盖调用方视图。
        this.viewportGizmoCameraAnimator?.cancel();

        this.syncCameraTransforms(position, target, up ?? this.camera.up.clone());
        void this.controls.setLookAt(
            position.x,
            position.y,
            position.z,
            target.x,
            target.y,
            target.z,
        );
        this.controls.update();
    }


    /**
     * 将当前相机缩放到所有可见项目。
     *
     * 设计说明：
     * 1. SolidWorks 的 F 快捷键属于视图导航命令，核心相机行为应收口在 CameraManager；
     * 2. 全图范围必须复用视图定向包围盒过滤规则，避免辅助对象、基准对象或定向器污染范围；
     * 3. 空场景直接返回，保持导航命令幂等。
     */
    public fitVisibleItems(): void {
        const bounds = this.resolveVisibleItemsBounds();
        if (!bounds) {
            return;
        }

        this.focusOnBounds(bounds);
    }

    /**
     * 根据对象包围盒动态聚焦相机。
     *
     * @param targetObject 目标对象；未传时使用当前主场景。
     * @param options 聚焦选项。
     */
    public focusOnObjectBounds(
        targetObject: THREE.Object3D | null = null,
        options: {
            /** 额外留白系数。 */
            padding?: number;
            /** 可选视线方向。 */
            direction?: THREE.Vector3;
            /** 可选相机上方向。 */
            up?: THREE.Vector3;
        } = {},
    ): void {
        if (!this.camera) {
            return;
        }

        const scene = this.viewer.getScene();
        const focusTarget = targetObject ?? scene;
        if (!focusTarget) {
            return;
        }

        const box = new THREE.Box3().setFromObject(focusTarget);
        this.focusOnBounds(box, options);
    }

    /**
     * 根据已解析包围盒聚焦相机。
     *
     * @param box 目标包围盒。
     * @param options 聚焦选项。
     */
    public focusOnBounds(
        box: THREE.Box3,
        options: {
            /** 额外留白系数。 */
            padding?: number;
            /** 可选视线方向。 */
            direction?: THREE.Vector3;
            /** 可选相机上方向。 */
            up?: THREE.Vector3;
        } = {},
    ): void {
        if (!this.camera) {
            return;
        }

        const focusResult = this.boundingBoxCameraFocusSolver.resolve({
            box,
            camera: this.camera,
            direction: options.direction,
            up: options.up,
            padding: options.padding,
        });
        if (!focusResult) {
            return;
        }

        if (this.camera instanceof THREE.OrthographicCamera && typeof focusResult.zoom === 'number') {
            this.camera.zoom = focusResult.zoom;
            this.camera.updateProjectionMatrix();
        }

        const distance = focusResult.position.distanceTo(focusResult.target);
        const size = box.getSize(new THREE.Vector3());
        const diagonal = Math.max(size.length(), 1e-3);

        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.near = Math.max(
                CameraManager.PERSPECTIVE_CAMERA_NEAR,
                Math.min(this.camera.near, distance * 0.05, diagonal * 0.1),
            );
            this.camera.far = CameraManager.PERSPECTIVE_CAMERA_FAR;
            this.camera.updateProjectionMatrix();
        }

        if (this.controls) {
            // 聚焦全图不能收紧用户后续导航范围，只恢复相机控制器统一边界。
            this.controls.minDistance = CameraManager.CAMERA_CONTROLS_MIN_DISTANCE;
            this.controls.maxDistance = CameraManager.CAMERA_CONTROLS_MAX_DISTANCE;
        }

        this.lookAtTarget(focusResult.position, focusResult.target, focusResult.up);
        this.controls?.update();
        this.viewer.requestRender();
    }

    /**
     * 刷新所有可见项目的包围盒缓存。
     *
     * 设计说明：
     * 1. 该方法与视图定向器尺寸计算共用同一套过滤规则；
     * 2. 只统计真实可渲染对象，排除 helper、基准对象、视图定向器和草图缩放覆盖层；
     * 3. 方法只更新 CameraManager 内部缓存，调用方按需决定是否 clone，避免旋转热路径产生额外 Box3。
     *
     * @returns 是否存在有效可见项目包围盒。
     */
    private refreshVisibleItemsBounds(): boolean {
        const scene = this.viewer.getScene();
        if (!scene) {
            this.resetSceneBoundsCache();
            return false;
        }

        this.sceneBounds.makeEmpty();
        this.modelSpatialQuery.forEachModel(scene, this.shouldParticipateInRotationFocusBounds,
            (_source, bounds) => { this.sceneBounds.union(bounds); });

        return this.syncSceneBoundsCacheFromCurrentBox();
    }


    /**
     * 刷新当前活动相机对应的视锥体缓存。
     *
     * 设计说明：
     * 1. 当前视口包围盒统计与中心关注对象解析都依赖同一套投影视图矩阵；
     * 2. 将视锥体刷新收口到一个入口，避免后续相机矩阵更新规则出现两套实现；
     * 3. 调用方在无相机时自行回退，因为回退策略取决于旋转中心解析阶段。
     */
    private refreshViewportFrustum(): void {
        this.camera.updateMatrixWorld(true);
        this.viewportProjectionMatrix.multiplyMatrices(
            this.camera.projectionMatrix,
            this.camera.matrixWorldInverse,
        );
        this.viewportFrustum.setFromProjectionMatrix(
            this.viewportProjectionMatrix,
            this.camera.coordinateSystem,
            this.camera.reversedDepth,
        );
    }

    /**
     * 刷新当前视口内真实可见项目的包围盒缓存。
     *
     * 设计说明：
     * 1. 旋转中心应跟随用户当前正在观察的局部范围，完全落在视锥体外的模型不应继续拉偏旋转中心；
     * 2. 对象过滤规则复用全图与视图定向范围规则，避免辅助对象、基准对象或隐藏层级污染旋转中心；
     * 3. `Frustum.intersectsBox()` 采用相交即保留语义，模型只有一部分进入屏幕时仍参与基础包围盒计算；
     * 4. 同步记录全场景真实模型是否都完整位于屏幕内，完整在屏幕内时必须保持包围盒旋转语义。
     *
     * @returns 是否存在有效视口内项目包围盒。
     */
    private refreshViewportVisibleItemsBounds(): boolean {
        const scene = this.viewer.getScene();
        if (!scene || !this.camera) {
            this.resetSceneBoundsCache();
            this.viewportRotationCoverageState = ViewportRotationCoverageState.Empty;
            return false;
        }

        this.refreshViewportFrustum();

        this.sceneBounds.makeEmpty();
        let hasRenderableBounds = false;
        let hasViewportBounds = false;
        let allRenderableBoundsFullyInsideViewport = true;

        this.modelSpatialQuery.forEachModel(scene, this.shouldParticipateInRotationFocusBounds, (_source, bounds) => {
            this.viewportCandidateBounds.copy(bounds);
            if (this.viewportCandidateBounds.isEmpty()) {
                return;
            }

            hasRenderableBounds = true;
            if (!this.isBoxFullyInsideViewportFrustum(this.viewportCandidateBounds)) {
                allRenderableBoundsFullyInsideViewport = false;
            }

            if (!this.viewportFrustum.intersectsBox(this.viewportCandidateBounds)) {
                return;
            }

            hasViewportBounds = true;
            this.sceneBounds.union(this.viewportCandidateBounds);
        });

        this.viewportRotationCoverageState = hasViewportBounds
            ? (
                hasRenderableBounds && allRenderableBoundsFullyInsideViewport
                    ? ViewportRotationCoverageState.AllInside
                    : ViewportRotationCoverageState.Clipped
            )
            : ViewportRotationCoverageState.Empty;
        return this.syncSceneBoundsCacheFromCurrentBox();
    }

    /**
     * 判断包围盒是否完整位于当前视锥体内。
     *
     * 设计说明：
     * 1. 判断“所有模型都在屏幕里”必须比 `intersectsBox()` 更严格；
     * 2. 只有包围盒八个角点全部位于视锥体内，才认为该对象没有被屏幕裁切；
     * 3. 任意对象被裁切或完全在屏幕外，都允许后续中心关注逻辑接管局部旋转中心。
     *
     * @param box 候选世界包围盒。
     * @returns 是否完整位于当前视锥体内。
     */
    private isBoxFullyInsideViewportFrustum(box: THREE.Box3): boolean {
        this.writeViewportCandidateBoundsCorners(box);
        return this.viewportCandidateBoundsCorners.every((corner) => (
            this.viewportFrustum.containsPoint(corner)
        ));
    }

    /**
     * 写入当前视口候选包围盒的八个角点。
     *
     * @param box 候选世界包围盒。
     */
    private writeViewportCandidateBoundsCorners(box: THREE.Box3): void {
        const { min, max } = box;
        this.viewportCandidateBoundsCorners[0].set(min.x, min.y, min.z);
        this.viewportCandidateBoundsCorners[1].set(min.x, min.y, max.z);
        this.viewportCandidateBoundsCorners[2].set(min.x, max.y, min.z);
        this.viewportCandidateBoundsCorners[3].set(min.x, max.y, max.z);
        this.viewportCandidateBoundsCorners[4].set(max.x, min.y, min.z);
        this.viewportCandidateBoundsCorners[5].set(max.x, min.y, max.z);
        this.viewportCandidateBoundsCorners[6].set(max.x, max.y, min.z);
        this.viewportCandidateBoundsCorners[7].set(max.x, max.y, max.z);
    }


    /**
     * 判断对象是否应参与可见项目包围盒统计。
     *
     * 设计说明：
     * 1. 全图、视图定向与旋转中心都需要同一套真实模型过滤规则；
     * 2. 该方法只判断对象资格，不修改任何包围盒缓存，避免遍历逻辑重复分散；
     * 3. Sprite 与带 geometry 的 Object3D 都可渲染，Group 等纯容器不直接参与统计。
     *
     * @param object 候选场景对象。
     * @returns 是否参与可见项目包围盒统计。
     */
    private shouldParticipateInVisibleItemsBounds(object: THREE.Object3D): boolean {
        if (!this.isObjectVisibleInHierarchy(object)) {
            return false;
        }

        // 全图命令、视图定向器和旋转中心必须一致，只统计真实模型对象。
        if (this.shouldIgnoreViewOrientationCoverageObject(object)) {
            return false;
        }

        const renderableObject = object as THREE.Object3D & {
            geometry?: THREE.BufferGeometry | null;
        };

        return object instanceof THREE.Sprite || Boolean(renderableObject.geometry);
    }

    /**
     * 重置场景包围盒缓存。
     */
    private resetSceneBoundsCache(): void {
        this.sceneBounds.makeEmpty();
        this.sceneBoundsCenter.set(0, 0, 0);
        this.sceneBoundsSize.set(0, 0, 0);
    }

    /**
     * 从当前 `sceneBounds` 同步中心与尺寸缓存。
     *
     * @returns 当前包围盒是否有效。
     */
    private syncSceneBoundsCacheFromCurrentBox(): boolean {
        if (this.sceneBounds.isEmpty()) {
            this.sceneBoundsCenter.set(0, 0, 0);
            this.sceneBoundsSize.set(0, 0, 0);
            return false;
        }

        this.sceneBounds.getCenter(this.sceneBoundsCenter);
        this.sceneBounds.getSize(this.sceneBoundsSize);
        return true;
    }

    /**
     * 判断对象在完整父级链中是否真实可见。
     *
     * 设计说明：
     * 1. three.js 的 `traverse()` 会继续访问隐藏父级下的子对象；
     * 2. 草图执行拉伸后会通过 `SketchGroup.visible = false` 隐藏整组，但子线段与控制点自身仍可能保持 `visible = true`；
     * 3. 包围盒统计必须使用层级可见性，避免隐藏草图继续影响旋转中心与全图范围。
     *
     * @param object 候选场景对象。
     * @returns 对象自身及全部父级是否可见。
     */
    private isObjectVisibleInHierarchy(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;

        while (current) {
            if (!current.visible) {
                return false;
            }

            current = current.parent;
        }

        return true;
    }

    /**
     * 解析所有可见项目的包围盒。
     *
     * 设计说明：
     * 1. 对外返回克隆后的 Box3，避免调用方意外修改 CameraManager 内部缓存；
     * 2. 旋转中心等热路径应直接使用 `refreshVisibleItemsBounds()` 后的缓存，避免额外分配。
     *
     * @returns 可见项目包围盒；没有有效项目时返回 null。
     */
    private resolveVisibleItemsBounds(): THREE.Box3 | null {
        if (!this.refreshVisibleItemsBounds()) {
            return null;
        }

        return this.sceneBounds.clone();
    }

    /**
     * 释放所有相机相关资源。
     */
    dispose(): void {
        this.clippingBoundsCache.dispose();
        this.modelSpatialQuery.dispose();
        this.viewportRotationFocusResolver.dispose();

        // 导航器必须先于 CameraControls 释放，动画取消和原生事件清理仍需要当前相机宿主。
        this.viewportGizmoCameraAnimator?.cancel();
        this.viewportGizmoCameraAnimator = null;
        if (this.viewportGizmo) {
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.StandardViewSelected,
                this.handleViewportGizmoStandardViewSelected,
            );
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.ControlActionTriggered,
                this.handleViewportGizmoControlActionTriggered,
            );
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.OrbitDragStarted,
                this.handleViewportGizmoOrbitDragStarted,
            );
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.OrbitDragChanged,
                this.handleViewportGizmoOrbitDragChanged,
            );
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.OrbitDragEnded,
                this.handleViewportGizmoOrbitDragEnded,
            );
            this.viewportGizmo.removeEventListener(
                ViewportGizmoEventType.HoverChanged,
                this.handleViewportGizmoVisualChanged,
            );
            this.viewportGizmo.dispose();
            this.viewportGizmo = null;
        }


        this.viewer.scene.removeEventListener(
            OrthographicClippingSceneEventType.ChildAdded,
            this.handleSceneContentChangedForOrthographicClipping,
        );
        this.viewer.scene.removeEventListener(
            OrthographicClippingSceneEventType.ChildRemoved,
            this.handleSceneContentChangedForOrthographicClipping,
        );
        this.controls?.removeEventListener('start', this.handleCameraControlsInteractionStart);
        this.controls?.removeEventListener('end', this.handleCameraControlsInteractionEnd);
        this.controls?.removeEventListener('change', this.handleCameraControlsUpdate);
        this.controls?.dispose();
        this.releaseRotationCenterLock();
        this.controls = null;
        this.controlsDomElement = null;
        if (this.viewOrientationInteractionController) {
            this.viewOrientationInteractionController.dispose();
            this.viewOrientationInteractionController = null;
        }
        if (this.viewOrientationMesh) {
            if (this.viewOrientationMesh.parent) {
                this.viewOrientationMesh.parent.remove(this.viewOrientationMesh);
            }
            this.viewOrientationMesh.dispose();
            this.viewOrientationMesh = null;
        }

        this.rotationCenterTopologyHighlightController.clear();
        this.disposeRotationCenterDebugVisuals();

        this.camera = null;
        this.perspectiveCamera = null;
        this.orthographicCamera = null;
        this.orthographicCameraDebugPanelInspector = null;
        this.orthographicCameraDebugPanelGroup = null;
        this.viewOrientationFocusSnapshot = null;
        this.viewOrientationFocusActive = false;

        this.viewOrientationPreviewRequestSubject.next(null);
        this.viewOrientationPreviewRequestSubject.complete();

    }

    /**
     * 重建 CameraControls 并保留 target 与 enabled 状态。
     *
     * @param enabled 控制器启用状态。
     * @param target 控制器目标点。
     */
    private recreateCameraControls(enabled: boolean, target: THREE.Vector3): void {
        this.controls?.removeEventListener('start', this.handleCameraControlsInteractionStart);
        this.controls?.removeEventListener('end', this.handleCameraControlsInteractionEnd);
        this.controls?.removeEventListener('change', this.handleCameraControlsUpdate);
        this.controls?.dispose();
        this.releaseRotationCenterLock();
        this.controls = this.createCameraControls(this.camera);
        this.controls.addEventListener('start', this.handleCameraControlsInteractionStart);
        this.controls.addEventListener('end', this.handleCameraControlsInteractionEnd);
        this.controls.addEventListener('change', this.handleCameraControlsUpdate);
        this.controls.enabled = enabled;
        void this.controls.setLookAt(
            this.camera.position.x,
            this.camera.position.y,
            this.camera.position.z,
            target.x,
            target.y,
            target.z,
        );
        this.applyMouseMode();
        this.controls.update();
        this.viewer.lightManager?.bindCameraControls(this.controls);
    }

    /**
     * 配置 Arc ballControls 鼠标动作。
     *
     * 设计说明：
     * 1. Arc ballControls 通过 `setMouseAction/unsetMouseAction` 管理输入映射，不再使用 `mouseButtons`；
     * 2. 左键映射必须清空，避免与选择、框选、拖动链路抢占同一个 pointerdown；
     * 3. Shift+滚轮/中键默认会触发 FOV 调整，此处清空以保持 PuppyCAD 当前缩放语义稳定；
     * 4. Ctrl+右键使用带修饰键的 PAN 映射，普通右键保留 ROTATE，交由 Arc ballControls 在事件中按修饰键解析。
     *
     * @param controls 需要配置的相机控制器。
     */
    private configureArcballMouseActions(controls: CameraControls): void {
        controls.unsetMouseAction(0);
        controls.unsetMouseAction(0, 'CTRL');
        controls.unsetMouseAction(1, 'SHIFT');
        controls.unsetMouseAction('WHEEL', 'SHIFT');
        controls.setMouseAction('ZOOM', 1);
        controls.setMouseAction('ZOOM', 'WHEEL');
        controls.setMouseAction('ROTATE', 2);
        controls.setMouseAction('PAN', 2, 'CTRL');
    }

    /**
     * 同步透视与正交相机的变换状态。
     *
     * @param position 相机位置。
     * @param target 观察目标。
     * @param up 朝上向量。
     */
    private syncCameraTransforms(position: THREE.Vector3, target: THREE.Vector3, up: THREE.Vector3): void {
        if (this.perspectiveCamera) {
            this.perspectiveCamera.position.copy(position);
            this.perspectiveCamera.up.copy(up);
            this.perspectiveCamera.lookAt(target);
            this.perspectiveCamera.updateProjectionMatrix();
        }
        if (this.orthographicCamera) {
            this.orthographicCamera.position.copy(position);
            this.orthographicCamera.up.copy(up);
            this.orthographicCamera.lookAt(target);
            // DOM 隐藏期可能短暂上报 0×0；程序化视图切换始终复用最后一次有效视口。
            this.updateOrthographicCameraFrustum(this.viewportWidth, this.viewportHeight);
        }

        const nextCamera = this.cameraType === CameraProjectionType.Orthographic
            ? this.orthographicCamera
            : this.perspectiveCamera;
        if (nextCamera) {
            this.camera = nextCamera;
            this.dispatchCameraStateChanged();
        }
    }

    /**
     * 同步视图定向器的当前激活态。
     */
    private syncViewOrientationMeshState(): void {
        if (!this.viewOrientationMesh || !this.camera || !this.controls) {
            return;
        }

        this.viewOrientationMesh.syncOpacityByCamera(this.camera.position, this.getControlsTarget());
        this.viewOrientationMesh.setActiveViewType(this.getCurrentStandardViewType());
    }

    /**
     * 基于主场景包围盒同步视图定向器尺寸。
     *
     * 设计说明：
     * 1. 只统计主场景中的可渲染对象，不把 helper 场景对象计入包围盒；
     * 2. 定向器中心跟随主场景包围盒中心，尺寸按包围盒最长边扩张；
     * 3. 仅在定向器显示时同步一次，避免每帧遍历场景造成性能浪费。
     */
    private syncViewOrientationMeshScale(): void {
        if (!this.viewOrientationMesh || !this.viewOrientationMesh.visible) {
            return;
        }

        const targetSize = this.resolveSceneCoverageSize();
        const scale = targetSize / ViewOrientationMesh.DEFAULT_SIZE;
        this.viewOrientationMesh.position.copy(this.sceneBoundsCenter);
        this.viewOrientationMesh.scale.setScalar(scale);
        this.viewOrientationMesh.updateMatrixWorld();
    }

    /**
     * 创建 CameraControls 实例。
     *
     * @param camera 目标相机。
     * @returns 相机控制器实例。
     */
    private createCameraControls(
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ): CameraControls {
        if (!this.controlsDomElement) {
            throw new Error('相机控制器创建失败：控制器 DOM 元素未就绪。');
        }

        const controls = new CameraControls(camera, this.controlsDomElement);
        controls.minDistance = CameraManager.CAMERA_CONTROLS_MIN_DISTANCE;
        controls.maxDistance = CameraManager.CAMERA_CONTROLS_MAX_DISTANCE;

        // CameraControls 保持场景无关，旋转中心由 CameraManager 注入当前视口内真实模型包围盒中心。
        controls.setRotationCenterResolver(this.resolveSceneRotationCenter);

        return controls;
    }


    /**
     * 应用正交相机下的视图定向聚焦缩放。
     *
     * 设计说明：
     * 1. 正交相机中物体屏幕尺寸主要由 zoom 决定，单纯移动相机无法让远距离定向器变大；
     * 2. 视图定向聚焦的目标是让定向器尽可能占满视口且保持完整可见；
     * 3. 因此根据定向器包围盒在当前相机 right/up 方向上的投影尺寸反推 zoom。
     *
     * @param bounds 视图定向器世界包围盒。
     * @param target 聚焦目标点。
     */
    private applyOrthographicViewOrientationFocusZoom(bounds: THREE.Box3, target: THREE.Vector3): void {
        if (!(this.camera instanceof THREE.OrthographicCamera)) {
            return;
        }

        const viewDirection = this.camera.position.clone().sub(target).normalize();
        const up = this.camera.up.clone().normalize();
        const right = new THREE.Vector3().crossVectors(viewDirection, up).normalize();
        const actualUp = right.clone().cross(viewDirection).normalize();
        const corners = CameraManager.collectBoxCorners(bounds);
        let halfWidth = 0;
        let halfHeight = 0;

        corners.forEach((corner) => {
            const relative = corner.clone().sub(target);
            halfWidth = Math.max(halfWidth, Math.abs(relative.dot(right)));
            halfHeight = Math.max(halfHeight, Math.abs(relative.dot(actualUp)));
        });

        const frustumWidth = Math.abs(this.camera.right - this.camera.left);
        const frustumHeight = Math.abs(this.camera.top - this.camera.bottom);
        const safeHalfWidth = Math.max(halfWidth, Number.EPSILON);
        const safeHalfHeight = Math.max(halfHeight, Number.EPSILON);
        const zoomByWidth = frustumWidth / (safeHalfWidth * 2 * CameraManager.VIEW_ORIENTATION_FOCUS_DISTANCE_MARGIN);
        const zoomByHeight = frustumHeight / (safeHalfHeight * 2 * CameraManager.VIEW_ORIENTATION_FOCUS_DISTANCE_MARGIN);

        this.camera.zoom = Math.max(Math.min(zoomByWidth, zoomByHeight), Number.EPSILON);
        this.camera.updateProjectionMatrix();
    }

    /**
     * 收集包围盒八个角点。
     *
     * @param box 目标包围盒。
     * @returns 包围盒角点列表。
     */
    private static collectBoxCorners(box: THREE.Box3): THREE.Vector3[] {
        return [
            new THREE.Vector3(box.min.x, box.min.y, box.min.z),
            new THREE.Vector3(box.min.x, box.min.y, box.max.z),
            new THREE.Vector3(box.min.x, box.max.y, box.min.z),
            new THREE.Vector3(box.min.x, box.max.y, box.max.z),
            new THREE.Vector3(box.max.x, box.min.y, box.min.z),
            new THREE.Vector3(box.max.x, box.min.y, box.max.z),
            new THREE.Vector3(box.max.x, box.max.y, box.min.z),
            new THREE.Vector3(box.max.x, box.max.y, box.max.z),
        ];
    }


    /**
     * 读取当前控制器目标点。
     *
     * @returns 当前控制器目标点副本。
     */
    private getControlsTarget(): THREE.Vector3 {
        if (!this.controls) {
            return new THREE.Vector3(0, 0, 0);
        }
        return this.controls.getTarget(this.controlsTargetCache).clone();
    }

    /**
     * 将相机调整到可完整观察视图定向器的位置。
     */
    private focusCameraToViewOrientationMesh(): void {
        if (!this.camera || !this.controls || !this.viewOrientationMesh) {
            return;
        }

        this.viewOrientationMesh.updateMatrixWorld(true);

        const bounds = new THREE.Box3().setFromObject(this.viewOrientationMesh);
        if (bounds.isEmpty()) {
            return;
        }

        const target = bounds.getCenter(new THREE.Vector3());
        const size = bounds.getSize(new THREE.Vector3());
        const radius = Math.max(size.length() * 0.5, Number.EPSILON);
        const viewDirection = this.camera.position.clone().sub(this.getControlsTarget());
        if (viewDirection.lengthSq() <= Number.EPSILON) {
            viewDirection.set(1, 1, 1);
        }
        viewDirection.normalize();

        let focusDistance = radius * 2;
        if (this.camera instanceof THREE.PerspectiveCamera) {
            const halfFov = THREE.MathUtils.degToRad(this.camera.fov) / 2;
            const safeHalfFov = Math.max(halfFov, THREE.MathUtils.degToRad(5));
            focusDistance = radius / Math.sin(safeHalfFov);
        }
        focusDistance *= CameraManager.VIEW_ORIENTATION_FOCUS_DISTANCE_MARGIN;

        const nextPosition = target.clone().add(viewDirection.multiplyScalar(focusDistance));
        this.lookAtTarget(nextPosition, target, this.camera.up.clone());

        if (this.camera instanceof THREE.OrthographicCamera) {
            this.applyOrthographicViewOrientationFocusZoom(bounds, target);
        }

        this.controls.update();
        this.viewer.requestRender();
    }

    /**
     * 从当前场景解析视口旋转中心。
     *
     * 设计说明：
     * 1. 先执行原有视口合并包围盒逻辑，保证完整全图观察时仍围绕整体包围盒旋转；
     * 2. 只有存在模型被屏幕裁切或完全离屏时，才启用中心关注 `FeatureResultMesh` 与拓扑代理；
     * 3. 没有明确中心关注对象时，继续使用第 1 步得到的当前视口真实模型合并包围盒；
     * 4. 该方法统一同步 `sceneBounds` 缓存和拓扑高亮目标，保证锁定、调试刷新和实际旋转使用同一结果。
     *
     * @param target 可复用写入缓存。
     * @returns 是否解析到有效场景旋转中心。
     */
    private resolveViewportRotationCenterFromScene(target: THREE.Vector3): boolean {
        this.resetRotationCenterTopologyHighlightTarget();

        if (!this.refreshViewportVisibleItemsBounds()) {
            return false;
        }

        if (
            this.viewportRotationCoverageState !== ViewportRotationCoverageState.AllInside
            && this.resolveFocusedViewportRotationCenter(target, false)
        ) {
            return true;
        }

        // sceneBoundsCenter 由 refreshViewportVisibleItemsBounds() 更新，直接复制缓存可避免旋转热路径 clone Box3。
        target.copy(this.sceneBoundsCenter);

        this.syncRotationCenterDebugVisuals(
            RotationCenterDebugVisualState.CenterOnly,
            target,
        );

        return true;
    }

    /**
     * 记录当前旋转中心可高亮的拓扑代理目标。
     *
     * 设计说明：
     * 1. `ViewportRotationFocusResolver` 会复用结果对象、包围盒和向量缓存；
     * 2. 旋转会话锁定期间只能保存不可变的拓扑身份信息，不能长期持有解析器内部引用；
     * 3. 只有旋转中心真的来自拓扑代理时才允许后续显示中心标记和拓扑高亮。
     *
     * @param focusResult 当前中心关注解析结果。
     */
    private captureRotationCenterTopologyHighlightTarget(focusResult: ViewportRotationFocusResult): void {
        if (
            focusResult.kind !== ViewportRotationFocusTargetKind.TopologyProxy
            || (!focusResult.featureResultMesh && !focusResult.topologySource)
            || !focusResult.topologyProxyKind
            || focusResult.topologyProxyIndex === null
        ) {
            this.resetRotationCenterTopologyHighlightTarget();
            return;
        }

        this.rotationCenterTopologyHighlightTarget = {
            featureResultMesh: focusResult.featureResultMesh,
            topologySource: focusResult.topologySource,
            scene: this.viewer.getScene(),
            topologyProxyKind: focusResult.topologyProxyKind,
            topologyProxyIndex: focusResult.topologyProxyIndex,
        };
    }

    /**
     * 清空当前旋转中心拓扑高亮目标。
     */
    private resetRotationCenterTopologyHighlightTarget(): void {
        this.rotationCenterTopologyHighlightTarget = null;
    }

    /**
     * 解析当前视口中心关注对象对应的旋转中心。
     *
     * 设计说明：
     * 1. 中心关注解析处理结果体及单个导入实例，无拓扑普通 Mesh 保留合并包围盒兜底；
     * 2. 解析器返回的包围盒可能是拓扑点、线、面代理，也可能是结果体自身；
     * 3. 如果中心来自拓扑代理，调试中心标识必须使用最终中心点，才能准确观察实际旋转中心来源。
     *
     * @param target 可复用写入缓存。
     * @param refreshFrustum 是否在解析前刷新视锥体；调用方已刷新时可关闭以避免重复更新矩阵。
     * @returns 是否解析到中心关注旋转中心。
     */
    private resolveFocusedViewportRotationCenter(
        target: THREE.Vector3,
        refreshFrustum: boolean = true,
    ): boolean {
        const scene = this.viewer.getScene();
        if (!scene || !this.camera) {
            return false;
        }

        if (refreshFrustum) {
            this.refreshViewportFrustum();
        }
        const focusResult = this.viewportRotationFocusResolver.resolve({
            scene,
            camera: this.camera,
            frustum: this.viewportFrustum,
            shouldParticipate: this.shouldParticipateInRotationFocusBounds,
            spatialQuery: this.modelSpatialQuery,
        });
        if (!focusResult) {
            return false;
        }

        this.captureRotationCenterTopologyHighlightTarget(focusResult);
        target.copy(focusResult.center);
        this.sceneBounds.copy(focusResult.bounds);
        this.syncSceneBoundsCacheFromCurrentBox();

        this.syncRotationCenterDebugVisuals(
            RotationCenterDebugVisualState.CenterOnly,
            target,
        );

        return true;
    }


    /**
     * 处理 CameraControls 交互开始事件。
     *
     * 设计说明：
     * 1. 只有旋转输入会消费旋转中心，缩放和平移不应创建旋转中心会话锁；
     * 2. 锁定必须发生在交互开始处，后续旋转过程中视口内容变化不能立即改写中心；
     * 3. 如果已经存在锁，说明同一触控/鼠标会话仍未结束，继续使用原锁定结果。
     */
    private readonly handleCameraControlsInteractionStart = (): void => {
        if (!this.controls?.isRotationInteractionActive()) {
            return;
        }

        this.lockRotationCenterForCurrentViewport();
    };

    /**
     * 处理 CameraControls 交互结束事件。
     *
     * 设计说明：
     * 1. 旋转会话结束后才能释放锁定中心，避免拖拽过程中出现中心跳变；
     * 2. 释放后隐藏调试中心标识与拓扑高亮，下一次旋转开始时再按新锁定结果判断是否显示。
     */
    private readonly handleCameraControlsInteractionEnd = (): void => {
        this.releaseRotationCenterLock();
    };

    /**
     * 锁定当前视口对应的旋转中心。
     */
    private lockRotationCenterForCurrentViewport(): void {
        if (this.rotationCenterLocked) {
            return;
        }

        if (this.resolveViewportRotationCenterFromScene(this.lockedRotationCenter)) {
            // resolveViewportRotationCenterFromScene 已将最终中心写入 lockedRotationCenter。
        } else if (this.controls) {
            this.resetRotationCenterTopologyHighlightTarget();
            this.controls.getTarget(this.lockedRotationCenter);
        } else {
            this.resetRotationCenterTopologyHighlightTarget();
            this.lockedRotationCenter.set(0, 0, 0);
        }

        this.rotationCenterLocked = true;
        this.syncLockedRotationCenterDebugVisuals(this.lockedRotationCenter);
    }

    /**
     * 释放当前旋转中心锁定。
     */
    private releaseRotationCenterLock(): void {
        if (!this.rotationCenterLocked) {
            return;
        }

        this.rotationCenterLocked = false;
        this.rotationCenterTopologyHighlightController.clear();
        this.resetRotationCenterTopologyHighlightTarget();
        this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.Hidden);
    }

    /**
     * 使用锁定数据同步旋转中心调试显示。
     *
     * @param center 当前锁定旋转中心。
     */
    private syncLockedRotationCenterDebugVisuals(center: THREE.Vector3): void {
        if (!this.syncLockedRotationCenterTopologyHighlight()) {
            this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.Hidden);
            return;
        }

        // 包围盒只用于锁定旋转中心计算，调试显示阶段默认隐藏包围盒线框，仅显示拓扑中心标识。
        this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.CenterOnly, center);
    }

    /**
     * 同步锁定旋转中心对应的拓扑代理高亮。
     *
     * @returns 当前是否存在可显示中心标记的拓扑代理高亮。
     */
    private syncLockedRotationCenterTopologyHighlight(): boolean {
        if (!this._showRotationCenterDebug || !this.rotationCenterLocked) {
            this.rotationCenterTopologyHighlightController.clear();
            return false;
        }

        return this.rotationCenterTopologyHighlightController.apply(this.rotationCenterTopologyHighlightTarget);
    }


    /**
     * 按当前视口状态刷新旋转中心调试显示。
     *
     * 设计说明：
     * 1. 该方法用于调试开关开启瞬间同步状态；
     * 2. 非旋转状态必须隐藏，避免调试对象常驻遮挡模型；
     * 3. 旋转会话已锁定且中心来自拓扑代理时才显示当前锁定中心标识，包围盒线框保持隐藏。
     */
    private refreshRotationCenterDebugVisuals(): void {
        if (!this._showRotationCenterDebug) {
            return;
        }

        if (this.rotationCenterLocked) {
            this.syncLockedRotationCenterDebugVisuals(this.lockedRotationCenter);
            return;
        }

        this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.Hidden);
    }

    /**
     * 同步旋转中心调试可视对象。
     *
     * @param state 调试显示状态。
     * @param center 当前旋转中心。
     */
    private syncRotationCenterDebugVisuals(
        state: RotationCenterDebugVisualState,
        center?: THREE.Vector3,
    ): void {
        if (state === RotationCenterDebugVisualState.Hidden) {
            this.rotationCenterDebugVisual?.update({ state });
            return;
        }

        if (!this._showRotationCenterDebug || !center) {
            return;
        }

        if (!this.rotationCenterLocked) {
            this.rotationCenterDebugVisual?.update({ state: RotationCenterDebugVisualState.Hidden });
            return;
        }

        const visual = this.ensureRotationCenterDebugVisual();
        if (!visual) {
            return;
        }

        visual.update({
            state: RotationCenterDebugVisualState.CenterOnly,
            center,
        });
    }

    /**
     * 确保旋转中心调试可视对象已挂载到当前场景。
     *
     * @returns 旋转中心调试可视对象；场景不存在时返回 null。
     */
    private ensureRotationCenterDebugVisual(): RotationCenterDebugVisual | null {
        const scene = this.viewer.getScene();
        if (!scene) {
            return null;
        }

        if (this.rotationCenterDebugVisual && this.rotationCenterDebugVisual.root.parent !== scene) {
            // Viewer 场景替换后旧辅助对象不能留在旧场景，必须释放后重建。
            this.rotationCenterDebugVisual.dispose();
            this.rotationCenterDebugVisual = null;
        }

        if (!this.rotationCenterDebugVisual) {
            this.rotationCenterDebugVisual = new RotationCenterDebugVisual(scene);
        }

        return this.rotationCenterDebugVisual;
    }

    /**
     * 释放旋转中心调试可视对象。
     */
    private disposeRotationCenterDebugVisuals(): void {
        this.rotationCenterDebugVisual?.dispose();
        this.rotationCenterDebugVisual = null;
    }


    /**
     * 处理视口 Gizmo 标准视图选择。
     *
     * @param event 标准视图选择事件。
     */
    private readonly handleViewportGizmoStandardViewSelected = (
        event: ViewportGizmoStandardViewSelectedEvent,
    ): void => {
        // 不传当前 up，确保导航器点击严格使用集中定义表中的标准朝上方向。
        const frame = this.buildStandardViewCameraFrame(event.viewType);
        if (!frame) {
            return;
        }

        this.startViewportGizmoCameraAnimation(frame, event.viewType);
    };

    /**
     * 处理视口 Gizmo 外部按钮动作。
     *
     * @param event 外部按钮动作事件。
     */
    private readonly handleViewportGizmoControlActionTriggered = (
        event: ViewportGizmoControlActionTriggeredEvent,
    ): void => {
        if (!this.controls || !this.camera) {
            return;
        }

        const frame = this.viewportGizmoCameraRotationSolver.solveControlAction(
            this.camera.position,
            this.controls.target,
            this.camera.up,
            event.action,
        );
        this.startViewportGizmoCameraAnimation(frame);
    };

    /** 处理中央方向立方体拖动开始并接管相机控制权。 */
    private readonly handleViewportGizmoOrbitDragStarted = (): void => {
        // 新拖动优先于未完成动画，取消后再记录真实的控制器启用状态。
        this.viewportGizmoCameraAnimator?.cancel();
        this.viewportGizmoNavigationEnabledBeforeDrag = this.controls?.enabled ?? true;
        if (this.controls) {
            this.controls.enabled = false;
        }
    };

    /**
     * 处理中央方向立方体拖动增量。
     *
     * @param event 拖动角度增量事件。
     */
    private readonly handleViewportGizmoOrbitDragChanged = (
        event: ViewportGizmoOrbitDragChangedEvent,
    ): void => {
        if (!this.controls || !this.camera) {
            return;
        }

        const frame = this.viewportGizmoCameraRotationSolver.solvePointerOrbit(
            this.camera.position,
            this.controls.target,
            this.camera.up,
            event.horizontalAngle,
            event.verticalAngle,
        );
        this.applyViewportGizmoCameraFrame(frame);
    };

    /** 处理中央方向立方体拖动结束并恢复默认相机控制权。 */
    private readonly handleViewportGizmoOrbitDragEnded = (): void => {
        if (this.controls) {
            this.controls.enabled = this.viewportGizmoNavigationEnabledBeforeDrag;
        }
        this.syncViewportGizmoNavigationState();
    };

    /**
     * 启动视口 Gizmo 相机过渡动画。
     *
     * @param frame 目标相机帧。
     * @param activeViewType 动画完成后同步的标准视图类型。
     */
    private startViewportGizmoCameraAnimation(
        frame: StandardViewCameraFrame,
        activeViewType: StandardViewType | null = null,
    ): void {
        if (!this.viewportGizmoCameraAnimator) {
            this.applyViewportGizmoCameraFrame(frame);
            this.viewOrientationMesh?.setActiveViewType(activeViewType);
            return;
        }

        this.viewportGizmoCameraAnimator.start(frame, () => {
            // 场景内视图定向器若处于显示态，应与左下角导航器共享最终激活方向。
            this.viewOrientationMesh?.setActiveViewType(activeViewType);
            this.viewer.requestRender();
        });
    }

    /**
     * 立即应用视口 Gizmo 计算出的相机帧。
     *
     * @param frame 需要写入当前相机和控制器的相机帧。
     */
    private applyViewportGizmoCameraFrame(frame: StandardViewCameraFrame): void {
        this.viewportGizmoFramePosition.set(...frame.position);
        this.viewportGizmoFrameTarget.set(...frame.target);
        this.viewportGizmoFrameUp.set(...frame.up);
        this.lookAtTarget(
            this.viewportGizmoFramePosition,
            this.viewportGizmoFrameTarget,
            this.viewportGizmoFrameUp,
        );
        this.syncOrthographicCameraClippingFromVisibleBounds();
        this.viewer.requestRender();
    }

    /**
     * 将动画器写入的活动相机状态同步到备用投影相机和 CameraControls。
     */
    private readonly syncViewportGizmoNavigationState = (): void => {
        if (!this.controls || !this.camera) {
            return;
        }

        // 两种投影相机必须共享方向和目标，后续切换投影时才能保持当前视图。
        this.syncCameraTransforms(
            this.camera.position,
            this.controls.target,
            this.camera.up,
        );
        this.controls.setLookAt(
            this.camera.position.x,
            this.camera.position.y,
            this.camera.position.z,
            this.controls.target.x,
            this.controls.target.y,
            this.controls.target.z,
        );
        this.controls.update();
        this.syncOrthographicCameraClippingFromVisibleBounds();
        this.viewer.requestRender();
    };

    /**
     * 解析当前视图旋转中心。
     *
     * 设计说明：
     * 1. 旋转交互期间优先使用会话开始时锁定的包围盒中心，避免拖拽过程中切换中心；
     * 2. 非锁定状态下先计算当前视口真实模型合并包围盒，完整全图观察时保持包围盒旋转；
     * 3. 只有存在模型被屏幕裁切或离屏时，才解析屏幕中心关注结果体与拓扑代理；
     * 4. 没有屏幕内模型时回退到当前 controls.target，避免旋转突然吸回世界原点；
     * 5. 控制器未就绪时才回退到世界原点，保证初始化阶段仍有确定中心。
     *
     * @param target 可复用写入缓存。
     * @returns 当前旋转中心。
     */
    private readonly resolveSceneRotationCenter = (target: THREE.Vector3): THREE.Vector3 => {
        if (this.rotationCenterLocked) {
            target.copy(this.lockedRotationCenter);

            // 旋转过程中视口内对象会持续变化，必须继续使用交互开始时锁定的中心和包围盒。
            this.syncLockedRotationCenterDebugVisuals(target);

            return target;
        }

        if (this.resolveViewportRotationCenterFromScene(target)) {
            return target;
        }

        if (this.controls) {
            this.controls.getTarget(target);

            this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.CenterOnly, target);

            return target;
        }

        target.set(0, 0, 0);

        this.syncRotationCenterDebugVisuals(RotationCenterDebugVisualState.CenterOnly, target);

        return target;
    };


    /**
     * 处理主场景内容变化，刷新正交相机裁剪安全范围。
     */
    private readonly handleSceneContentChangedForOrthographicClipping = (): void => {
        this.syncOrthographicCameraClippingFromVisibleBounds();
        this.viewer.requestRender();
    };

    /** 导航器仅修改自身材质时也应唤醒按需绘制，不能再依赖主场景一直重绘。 */
    private readonly handleViewportGizmoVisualChanged = (): void => {
        this.viewer.requestRender();
    };

    /**
     * 处理 CameraControls 更新事件，同步相机派生状态。
     */
    private readonly handleCameraControlsUpdate = (): void => {
        if (!this.controls) {
            return;
        }

        this.syncOrthographicCameraClippingFromVisibleBounds();


        // 固定像素显示对象统一订阅 StateChanged；CameraManager 只发布相机状态，避免感知具体手柄模块。
        this.dispatchCameraStateChanged();
    };

    /**
     * 解析正交相机动态裁剪单侧深度留白。
     *
     * 设计说明：
     * 1. 用户期望裁剪范围按照可见包围盒深度的 1.2 倍处理；
     * 2. near 与 far 分别控制包围盒前后两侧，所以额外 0.2 倍深度需要平均分配到两侧；
     * 3. 极薄模型仍保留最小深度留白，避免深度跨度接近 0 时裁剪面贴住模型。
     *
     * @param depthSpan 可见包围盒在当前视线方向上的深度跨度。
     * @returns 单侧深度留白。
     */
    private resolveOrthographicClippingDepthMargin(depthSpan: number): number {
        // 按 1.2 倍包围盒深度得到扩展后的目标深度范围。
        const scaledDepthSpan = depthSpan * CameraManager.ORTHOGRAPHIC_CLIPPING_BOUNDS_DEPTH_SCALE;
        // 将扩展出来的深度平均分给 near 与 far 两侧，保证模型前后裁剪余量一致。
        const scaledDepthMargin = (scaledDepthSpan - depthSpan) / 2;

        return Math.max(scaledDepthMargin, CameraManager.ORTHOGRAPHIC_CLIPPING_MIN_DEPTH_MARGIN);
    }

    /**
     * 解析正交相机动态裁剪使用的视线反方向。
     * @param target 当前控制器目标点。
     * @returns 从目标点指向相机的单位方向。
     */
    private resolveOrthographicClippingViewDirection(target: THREE.Vector3): THREE.Vector3 {
        const viewDirection = this.orthographicClippingViewDirection;

        if (this.orthographicCamera) {
            viewDirection.copy(this.orthographicCamera.position).sub(target);
            if (viewDirection.lengthSq() > Number.EPSILON) {
                return viewDirection.normalize();
            }

            this.orthographicCamera.getWorldDirection(viewDirection);
            if (viewDirection.lengthSq() > Number.EPSILON) {
                return viewDirection.multiplyScalar(-1).normalize();
            }
        }

        return viewDirection.set(0, 0, 1);
    }

    /**
     * 解析包围盒在正交相机视线反方向上的投影范围。
     * @param bounds 可见模型包围盒。
     * @param target 当前控制器目标点。
     * @param viewDirection 从目标点指向相机的单位方向。
     * @returns 深度投影范围；包围盒非法时返回 `null`。
     */
    private resolveOrthographicClippingDepthRange(
        bounds: THREE.Box3,
        target: THREE.Vector3,
        viewDirection: THREE.Vector3,
    ): { min: number; max: number } | null {
        if (bounds.isEmpty()) {
            return null;
        }

        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        const updateRange = (x: number, y: number, z: number): void => {
            const depth = this.orthographicClippingCornerProjection
                .set(x, y, z)
                .sub(target)
                .dot(viewDirection);
            min = Math.min(min, depth);
            max = Math.max(max, depth);
        };

        updateRange(bounds.min.x, bounds.min.y, bounds.min.z);
        updateRange(bounds.min.x, bounds.min.y, bounds.max.z);
        updateRange(bounds.min.x, bounds.max.y, bounds.min.z);
        updateRange(bounds.min.x, bounds.max.y, bounds.max.z);
        updateRange(bounds.max.x, bounds.min.y, bounds.min.z);
        updateRange(bounds.max.x, bounds.min.y, bounds.max.z);
        updateRange(bounds.max.x, bounds.max.y, bounds.min.z);
        updateRange(bounds.max.x, bounds.max.y, bounds.max.z);

        if (!Number.isFinite(min) || !Number.isFinite(max)) {
            return null;
        }

        return { min, max };
    }

    /**
     * 解析正交相机动态裁剪近裁剪面。
     * @param currentDistance 当前相机到控制器目标点的距离。
     * @param maxDepthOffset 包围盒朝相机方向的最大深度偏移。
     * @param depthMargin 深度留白。
     * @returns 安全近裁剪面。
     */
    private resolveOrthographicClippingNear(
        currentDistance: number,
        maxDepthOffset: number,
        depthMargin: number,
    ): number {
        const currentFrontDistance = Math.max(currentDistance - maxDepthOffset, CameraManager.ORTHOGRAPHIC_CAMERA_NEAR);
        const maxAllowedNear = Math.max(currentFrontDistance - depthMargin, CameraManager.ORTHOGRAPHIC_CAMERA_NEAR);
        const currentNear = this.orthographicCamera?.near ?? CameraManager.ORTHOGRAPHIC_CAMERA_NEAR;
        return Math.max(
            CameraManager.ORTHOGRAPHIC_CAMERA_NEAR,
            Math.min(currentNear, maxAllowedNear),
        );
    }

    /**
     * 根据当前可见模型包围盒同步正交相机裁剪安全范围。
     *
     * 设计说明：
     * 1. 正交投影视图大小由 `zoom` 决定，沿视线方向移动相机不会改变屏幕构图；
     * 2. 但 near/far 裁剪仍然依赖相机到模型的实际深度距离，旋转后相机太近会裁掉模型；
     * 3. 因此这里复用视图定向的可见模型包围盒过滤规则，只调整相机沿视线方向的距离和裁剪面，不改 `zoom`；
     * 4. 裁剪距离、视图定向预览、旋转中心必须共用“可见模型范围”，避免隐藏基准对象污染相机物理距离。
     */
    private syncOrthographicCameraClippingFromVisibleBounds(): void {
        if (!(this.camera instanceof THREE.OrthographicCamera) || !this.orthographicCamera || !this.controls) {
            return;
        }


        const scene = this.viewer.getScene();
        const bounds = this.clippingWorldBounds;
        if (!scene || !this.clippingBoundsCache.copyBounds(scene, bounds)) {
            return;
        }

        const target = this.controls.getTarget(this.orthographicClippingTarget);
        const viewDirection = this.resolveOrthographicClippingViewDirection(target);
        const depthRange = this.resolveOrthographicClippingDepthRange(bounds, target, viewDirection);
        if (!depthRange) {
            return;
        }

        const depthSpan = Math.max(depthRange.max - depthRange.min, CameraManager.ORTHOGRAPHIC_CLIPPING_MIN_DEPTH_MARGIN);
        const depthMargin = this.resolveOrthographicClippingDepthMargin(depthSpan);
        const currentDistance = Math.max(this.orthographicCamera.position.distanceTo(target), Number.EPSILON);
        const safeNear = this.resolveOrthographicClippingNear(currentDistance, depthRange.max, depthMargin);
        const requiredDistance = Math.max(currentDistance, depthRange.max + safeNear + depthMargin);
        const nextPosition = this.orthographicClippingCornerProjection
            .copy(target)
            .add(viewDirection.multiplyScalar(requiredDistance));
        const nextFar = Math.max(
            CameraManager.ORTHOGRAPHIC_CAMERA_FAR,
            requiredDistance - depthRange.min + depthMargin,
            safeNear + depthMargin,
        );
        const positionChanged = this.orthographicCamera.position.distanceToSquared(nextPosition) > Number.EPSILON;
        const nearChanged = Math.abs(this.orthographicCamera.near - safeNear) > Number.EPSILON;
        const farChanged = Math.abs(this.orthographicCamera.far - nextFar) > Number.EPSILON;

        if (!positionChanged && !nearChanged && !farChanged) {
            return;
        }

        this.orthographicCamera.position.copy(nextPosition);
        this.orthographicCamera.near = safeNear;
        this.orthographicCamera.far = nextFar;
        this.orthographicCamera.updateProjectionMatrix();
        this.orthographicCamera.updateMatrixWorld(true);
        this.controls.setPosition(nextPosition.x, nextPosition.y, nextPosition.z);
        this.viewer.requestRender();
    }


    /**
     * 解析空场景下视图定向器覆盖边长。
     *
     * 设计说明：
     * 1. 空场景没有真实模型包围盒时，不能继续使用视图定向器默认尺寸，否则会把相机拉到远离轴线的位置；
     * 2. 可见场景轴线是空场景里唯一稳定的尺寸参照，因此使用它的世界包围盒作为视图定向目标；
     * 3. 该逻辑只在无真实模型时启用，不改变真实模型统计阶段对 GridHelper / AxesHelper 的排除规则。
     *
     * @returns 空场景目标外接边长。
     */
    private resolveEmptySceneCoverageSize(): number {
        const axesHelper = this.viewer.helperManager?.getAxesHelper() ?? null;
        if (!axesHelper || !axesHelper.visible) {
            return this.resolveEmptySceneMinimumCoverageSize();
        }

        // 轴线可能跟随父级变换，计算前必须刷新世界矩阵，确保包围盒反映当前可见尺寸。
        axesHelper.updateMatrixWorld(true);
        this.sceneBounds.setFromObject(axesHelper);
        if (this.sceneBounds.isEmpty()) {
            return this.resolveEmptySceneMinimumCoverageSize();
        }

        this.sceneBounds.getCenter(this.sceneBoundsCenter);
        this.sceneBounds.getSize(this.sceneBoundsSize);

        return Math.max(
            this.sceneBoundsSize.x,
            this.sceneBoundsSize.y,
            this.sceneBoundsSize.z,
            CameraManager.VIEW_ORIENTATION_EMPTY_SCENE_MIN_SIZE,
        ) * CameraManager.VIEW_ORIENTATION_EMPTY_SCENE_AXIS_MARGIN;
    }

    /**
     * 解析空场景下轴线不可用时的最小覆盖边长。
     *
     * @returns 空场景最小目标外接边长。
     */
    private resolveEmptySceneMinimumCoverageSize(): number {
        // 轴线不可见、未初始化或包围盒无效时，使用与默认轴线尺寸一致的最小值，避免退回 18 造成视图过度缩小。
        this.sceneBoundsCenter.set(0, 0, 0);
        this.sceneBoundsSize.setScalar(CameraManager.VIEW_ORIENTATION_EMPTY_SCENE_MIN_SIZE);
        return CameraManager.VIEW_ORIENTATION_EMPTY_SCENE_MIN_SIZE * CameraManager.VIEW_ORIENTATION_EMPTY_SCENE_AXIS_MARGIN;
    }


    /**
     * 判断对象是否应从视图定向包围盒统计中排除。
     *
     * 设计说明：
     * 1. 视图定向器自身不能参与包围盒，否则会在显示后不断影响自己的尺寸；
     * 2. HelperManager 与调试工具会用 `ignore` 标记辅助对象，这些对象不代表模型真实尺寸；
     * 3. 基准面、基准轴、基准点及其子对象是参考对象，不是模型实体，不能参与视图定向目标范围；
     * 4. 草图缩放覆盖层只服务草图编辑提示，不代表模型实体尺寸，必须连同子对象整体排除；
     * 5. GridHelper / AxesHelper 即使缺少 `ignore` 标记，也应作为辅助对象排除，避免小模型被大网格范围淹没。
     * 6. 无限长草图直线没有有限模型范围，不能使用其渲染延伸长度污染包围盒。
     *
     * @param object 候选场景对象。
     * @returns 是否应从视图定向包围盒统计中排除。
     */
    private shouldIgnoreViewOrientationCoverageObject(object: THREE.Object3D): boolean {
        // 视图定向器自身属于交互工具，不是主模型内容。
        if (this.isViewOrientationSceneObject(object)) {
            return true;
        }

        // 统一尊重现有辅助对象约定，避免把调试盒、网格等工具对象纳入模型范围。
        if ((object as unknown as { ignore?: boolean }).ignore === true) {
            return true;
        }

        // 基准面会包含边框 LineSegments 和标签 Object3D，必须按父级链整体排除。
        if (this.isDatumReferenceSceneObject(object)) {
            return true;
        }

        // 草图缩放覆盖层是 UI 辅助对象，按父级链整体排除，避免覆盖层线框放大视图定向范围。
        if (this.isNamedAncestorSceneObject(object, CameraManager.SKETCH_SCALE_OVERLAY_ROOT_NAME)) {
            return true;
        }

        // 无限长直线的渲染端点仅用于显示，不能作为真实模型范围参与视图计算。
        if (isSketchLineObject(object) && object.isInfiniteLength) {
            return true;
        }

        // GridHelper / AxesHelper 是典型辅助对象，显式排除可防止缺少 ignore 标记时污染包围盒。
        return object instanceof THREE.GridHelper || object instanceof THREE.AxesHelper;
    }


    /**
     * 判断对象是否属于基准参考对象层级。
     *
     * 设计说明：
     * 1. 默认前视、上视、右视基准面根对象会被 `isDatumPlaneObject` 命中；
     * 2. 基准面 mesh、边框与标签作为子对象存在，单独遍历时可能只有局部标记；
     * 3. 因此必须沿父级链检查，确保基准面、基准轴、基准点及其全部子对象都不会污染模型包围盒。
     *
     * @param object 候选场景对象。
     * @returns 是否属于基准参考对象层级。
     */
    private isDatumReferenceSceneObject(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isDatumPlaneObject(current) || isDatumPlaneMesh(current) || isDatumAxis(current) || isDatumPoint(current)) {
                return true;
            }
            current = current.parent;
        }

        return false;
    }


    /**
     * 判断对象自身或父级链上是否存在指定名称对象。
     *
     * 设计说明：
     * 1. 部分辅助对象以根 Group 名称作为稳定识别协议；
     * 2. 这些辅助对象的子对象通常只是线段、点或标签，本身没有业务标记；
     * 3. 因此必须沿父级链判断根对象名称，确保整棵辅助对象层级都不参与模型包围盒。
     *
     * @param object 候选场景对象。
     * @param name 需要匹配的父级或自身名称。
     * @returns 是否命中指定名称对象层级。
     */
    private isNamedAncestorSceneObject(object: THREE.Object3D, name: string): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current.name === name) {
                return true;
            }
            current = current.parent;
        }

        return false;
    }


    /**
     * 判断对象是否属于视图定向器自身层级。
     *
     * @param object 候选场景对象。
     * @returns 是否为视图定向器自身对象。
     */
    private isViewOrientationSceneObject(object: THREE.Object3D): boolean {
        if (!this.viewOrientationMesh) {
            return false;
        }

        let current: THREE.Object3D | null = object;
        while (current) {
            if (current === this.viewOrientationMesh) {
                return true;
            }
            current = current.parent;
        }

        return false;
    }


    /**
     * 创建正交相机。
     * @param aspect 宽高比。
     * @param position 相机位置。
     * @param target 观察目标。
     * @returns 正交相机实例。
     */
    private createOrthographicCamera(
        aspect: number,
        position: THREE.Vector3,
        target: THREE.Vector3
    ): THREE.OrthographicCamera {
        const distance = position.distanceTo(target);
        const frustumHeight = 2 * distance * Math.tan(THREE.MathUtils.degToRad(this.options.fov / 2));
        const frustumWidth = frustumHeight * aspect;
        const camera = new THREE.OrthographicCamera(
            -frustumWidth / 2,
            frustumWidth / 2,
            frustumHeight / 2,
            -frustumHeight / 2,
            CameraManager.ORTHOGRAPHIC_CAMERA_NEAR,
            CameraManager.ORTHOGRAPHIC_CAMERA_FAR,
        );
        camera.position.copy(position);
        camera.lookAt(target);
        camera.updateProjectionMatrix();
        return camera;
    }

    /**
     * 更新正交相机视锥体。
     * @param width 视口宽度。
     * @param height 视口高度。
     */
    private updateOrthographicCameraFrustum(width: number, height: number): void {
        if (
            !this.orthographicCamera
            || !Number.isFinite(width)
            || !Number.isFinite(height)
            || width <= 0
            || height <= 0
        ) {
            return;
        }
        const aspect = width / height;

        /**
         * 设计说明：
         * 1. 正交相机的视图比例由 `frustum / zoom` 共同决定，resize 只能改变宽高比，不能重置用户或 fit view 得到的缩放；
         * 2. 旧逻辑按“相机到 target 的距离”重建 frustum，会在导入模型完成后的布局 resize 中把已聚焦模型重新缩小；
         * 3. 因此这里保持当前可见世界高度不变，只按新 aspect 更新水平范围。
         */
        const zoom = Math.max(this.orthographicCamera.zoom, 1e-6);
        const currentFrustumHeight = Math.abs(this.orthographicCamera.top - this.orthographicCamera.bottom);
        const currentVisibleHeight = currentFrustumHeight / zoom;
        const fallbackTarget = this.getControlsTarget();
        const fallbackDistance = this.orthographicCamera.position.distanceTo(fallbackTarget);
        const fallbackFrustumHeight = 2 * fallbackDistance * Math.tan(THREE.MathUtils.degToRad(this.options.fov / 2));
        const frustumHeight = Number.isFinite(currentVisibleHeight) && currentVisibleHeight > 1e-6
            ? currentVisibleHeight * zoom
            : fallbackFrustumHeight;
        const frustumWidth = frustumHeight * aspect;
        this.orthographicCamera.left = -frustumWidth / 2;
        this.orthographicCamera.right = frustumWidth / 2;
        this.orthographicCamera.top = frustumHeight / 2;
        this.orthographicCamera.bottom = -frustumHeight / 2;
        this.orthographicCamera.updateProjectionMatrix();
    }

    /**
     * 计算能包围主场景全部物体的视图定向器外接边长。
     *
     * 设计说明：
     * 1. 有真实模型时，尺寸只由过滤后的模型包围盒决定，避免辅助对象污染视图范围；
     * 2. 空场景时，尺寸使用场景轴线包围盒兜底，避免继续使用视图定向器自身默认尺寸导致轴线不可见。
     *
     * @returns 目标外接边长。
     */
    private resolveSceneCoverageSize(): number {
        const bounds = this.resolveVisibleItemsBounds();

        if (!bounds) {
            return this.resolveEmptySceneCoverageSize();
        }


        return Math.max(
            this.sceneBoundsSize.x,
            this.sceneBoundsSize.y,
            this.sceneBoundsSize.z,
        ) * CameraManager.VIEW_ORIENTATION_BOUNDING_MARGIN;
    }
}
